/* Badge geometry from the real die, matching the production renderer Simon shared
 * (assets/bb-production-preview.js on the customizer pilot theme).
 *
 * That renderer draws the badge as SVG at 72 units per inch and derives every position
 * from physical dimensions. We draw a raster photo with an HTML plate over it, so we
 * cannot adopt its geometry wholesale - but we can place the plate and the watermark by
 * the same fractions, measured against the badge artwork rather than the image canvas.
 *
 * Its formulas, reproduced here exactly:
 *   stripX = (dieWidth - stripWidth) / 2                  horizontally centred
 *   stripY = dieHeight - stripBottomInset - stripHeight
 *   watermark1 baseline = slotY + slotHeight + 0.12in
 *   watermark2 baseline = watermark1 + 5.2pt + 0.02in
 * Checked against its rendered SVG for the standard horizontal: strip 11.000 / 139.229 /
 * 221.0 / 72.0 and watermark baselines 33.881 / 40.520 in a 243 x 222.75 viewBox. All
 * four reproduce to three decimals.
 *
 * Badge Bottoms are deliberately absent: the production renderer has no profile for them,
 * so those two variants keep their existing placement rather than being given invented
 * numbers.
 */
var BB_POINTS_PER_INCH = 72;

// XL strips sit exactly where the standard strips do (BB placement rules v1, Simon
// 2026-10-09: "an XL and a standard Badge Buddy of the same orientation must look
// exactly the same at the bottom"; layout pack rules.strip.bottom_inset_from_cut_in).
// The XL rows used to carry the older production XL insets (H-XL 0.139993 in, V-XL
// 0.127573 in), which put the XL strip 0.51 mm lower (H) / 0.17 mm higher (V) than the
// text layout expects. scripts/bb-layout/check-designer.mjs checks every number here
// that the layout depends on against the pack's dies.
var BB_DIE = {
  'H-STD': { dieW: 3.375, dieH: 3.09375, cornerRadius: 0.1025, slotW: 0.553264, slotH: 0.125, slotTop: 0.225563, stripW: 3.069444, stripH: 1, stripBottomInset: 0.160014 },
  'V-STD': { dieW: 2.125, dieH: 4.5,     cornerRadius: 0.1025, slotW: 0.553264, slotH: 0.125, slotTop: 0.185792, stripW: 1.875,    stripH: 1.25, stripBottomInset: 0.120956 },
  'H-XL':  { dieW: 3.375, dieH: 4.09375, cornerRadius: 0.1025, slotW: 0.553264, slotH: 0.125, slotTop: 0.225564, stripW: 3.069444, stripH: 1, stripBottomInset: 0.160014 },
  'V-XL':  { dieW: 2.125, dieH: 5.5,     cornerRadius: 0.1025, slotW: 0.553264, slotH: 0.125, slotTop: 0.185768, stripW: 1.875,    stripH: 1.25, stripBottomInset: 0.120956 },
  // Badge Bottoms (Simon 2026-10-12, template product.custom-badge-bottoms only): the
  // finished strip is the whole die - no slot, no card, no watermark. Nominal finished
  // size = the layout pack's H-BBOTTOM / V-BBOTTOM dies and the proof labels.
  'H-BBOTTOM': { dieW: 3.5,   dieH: 1.3125, cornerRadius: 0.0625, slotW: 0, slotH: 0, slotTop: 0, stripW: 3.5,   stripH: 1.3125, stripBottomInset: 0 },
  'V-BBOTTOM': { dieW: 2.375, dieH: 1.3125, cornerRadius: 0.0625, slotW: 0, slotH: 0, slotTop: 0, stripW: 2.375, stripH: 1.3125, stripBottomInset: 0 }
};

// A Badge Bottom die: the strip is the whole piece and the engine's 'bottom' rule
// lays the title out (2 mm top/bottom, 5 mm sides, 77 pt, 45 pt per line on two lines).
function bbIsBottomDie(key) {
  return /-BBOTTOM$/.test(String(key || ''));
}

var BB_TYPE_TO_DIE = {
  'Badge Buddy - Horizontal': 'H-STD',
  'Badge Buddy Clear - Horizontal': 'H-STD',
  'Badge Buddy - Vertical': 'V-STD',
  'Badge Buddy Clear - Vertical': 'V-STD',
  'Badge Buddy - XL - Horizontal': 'H-XL',
  'Badge Buddy - XL - Vertical': 'V-XL'
};

function bbDieKey() {
  // The die-drawn stage names its die (snippets/bb-stage-svg.liquid). That is the only
  // way a Badge Bottom gets one: its die stage exists on product.custom-badge-bottoms
  // only, so Badge Bottoms on every other template keep the photo stage.
  var stage = document.querySelector('#tab2[data-bb-die]');
  var staged = stage && stage.getAttribute('data-bb-die');
  if (staged && BB_DIE[staged]) return staged;
  var el = document.querySelector('.custom_bb_type');
  return el && el.value ? (BB_TYPE_TO_DIE[el.value.trim()] || null) : null;
}

function bbDieProfile() {
  var key = bbDieKey();
  return key ? BB_DIE[key] : null;
}

/* Body/slot/strip geometry in die coordinates (points, 72 to the inch) - the same
 * shape and field names as the production renderer's own calculateGeometry(), so a
 * Node harness can diff the two outputs directly. Unlike the renderer this only runs
 * once per die, not once per FORMAT_CONFIG key, because our six variants map onto
 * just four dies (Clear shares H-STD/V-STD with the plain colour variants).
 */
// SPID-282 Phase E: the order payload's own version number, next to the geometry
// it describes since that is exactly what it tracks - bump it whenever this
// geometry, the fitting maths below (now bbAutoLayout and the layout pack it reads)
// or the font tables change, so Simon and Odin's print
// pipeline can tell an old recipe from a new one.
//
// SPID-282 Phase C2: bumped 1 -> 2. Phase C's image was clamped to a safe zone
// below the watermark and above the bar, so image.x/y/w/h could never leave that
// box. That clamp is gone - the image can now be dragged and zoomed past every
// edge of the card - so production must independently clip x/y/w/h to (the card
// shape) AND (the area above the bar) instead of trusting them to already be
// in-bounds. That is a real change in what the numbers mean, not just new data,
// which is exactly the case the spec says to bump v for. A new image.fit field
// (the zoom percentage the image was placed at) rides alongside so the exact
// placement can be reproduced without re-deriving it from x/y/w/h alone.
//
// SPID-282 Phase C4: bumped 2 -> 3. Pratik brought back an independent move/
// resize FRAME alongside the zoom (SPID-282 Phase C4 plan: "I want original
// moving and sizing feature along with this zoom in/out new feature"), so
// image.x/y/w/h now describe that FRAME - the region of the badge the artwork
// occupies - not the image's own placed rect the way v2 used them. That is again
// a real change in what those four numbers mean, not just new data: production
// must now clip the recipe to (the card shape) AND (the FRAME), not the whole
// area above the bar. Two new fields ride alongside, keeping every existing key
// name: image.offsetX/offsetY, the image's own centre minus the frame's centre
// in die points - what production needs, together with image.fit (unchanged
// meaning, still a zoom percentage of a fit scale), to reproduce exactly what
// part of the image the frame shows.
//
// BB placement rules v1: bumped 3 -> 4. The automatic text layout is now production's
// own layout engine (assets/bb-layout-engine.js reading assets/bb-layout-pack.js), so
// lines[].fontSize/x/baselineY come from different rules than in v3. Every v3 key is
// still there with the same meaning (die points, y down from the die top). New:
// rulesVersion, layout (the engine's own result in its own units: inches, x from the
// die centre, y up from the bottom cut, unrounded) and lines[].glyphX (where each
// character is drawn, die points). unsupportedChars now lists the characters the
// chosen font lacks, not the characters Minion Pro lacks.
var BB_DESIGN_PAYLOAD_VERSION = 4;

function bbCalculateGeometry(dieKey) {
  var die = dieKey ? BB_DIE[dieKey] : null;
  if (!die) return null;

  var dieWidth = bbInches(die.dieW), dieHeight = bbInches(die.dieH);
  var stripWidth = bbInches(die.stripW), stripHeight = bbInches(die.stripH);
  var stripX = (dieWidth - stripWidth) / 2;
  var stripY = dieHeight - bbInches(die.stripBottomInset) - stripHeight;
  var slotWidth = bbInches(die.slotW), slotHeight = bbInches(die.slotH);

  return {
    dieWidth: dieWidth, dieHeight: dieHeight, cornerRadius: bbInches(die.cornerRadius),
    stripX: stripX, stripY: stripY, stripWidth: stripWidth, stripHeight: stripHeight,
    stripBottom: stripY + stripHeight, stripCenterX: dieWidth / 2, stripCornerRadius: bbInches(0.1),
    slotX: (dieWidth - slotWidth) / 2, slotY: bbInches(die.slotTop),
    slotWidth: slotWidth, slotHeight: slotHeight
  };
}

/* Badge text sizing and placement, ported from the production renderer Simon shared
 * (assets/bb-production-preview.js, RENDERER_VERSION bb-production-preview-v6-2026-08-20).
 *
 * His 2026-08-19 note asked for "the color box and text sizing and positioning logic".
 * The colour box was matched by the die profiles above; this is the other half. What was
 * here before - jQuery textfill shrinking a span until it fitted - is an unrelated
 * algorithm that happened to produce text of roughly the right size, so the preview did
 * not show the sizes production actually prints.
 *
 * The renderer works in points at 72 to the inch and derives sizes from a table of Minion
 * Pro Bold glyph advances, copied here verbatim from its bundle. That matters twice over:
 * it is how the point sizes are computed, and it is what `textLength` is set from, which
 * forces the drawn text to occupy the width Minion Pro would occupy even though the font
 * never loads on this site and the browser falls back to Times New Roman. So the preview
 * shows the printed extent rather than the extent of whatever font happens to be present.
 *
 * Ported unchanged: the advance table, the two orientation profiles, calculateFontSizes,
 * computeTextLayout and the three baseline lifts. The only deliberate difference is the
 * coordinate space - the renderer positions text in die coordinates, we position it
 * inside the strip, which is the same geometry shifted by stripX/stripY and cancels out.
 *
 * Badge Bottoms have no profile in the renderer, so they keep the textfill path.
 *
 * BB placement rules v1 (2026-10-10): the computeTextLayout port that used to follow
 * (0.70 cap, 0.21 descender lift, Line 1 descender gap, short-caps lift, textLength
 * stretching) is gone. The Badge Buddy dies are laid out by production's layout engine
 * instead - see bbAutoLayout below. The tables here stay for measuring a moved/resized
 * line in a font the layout pack does not have, and for Badge Bottoms. */
var BB_REFERENCE_FONT_SIZE = 20;

var BB_MINION_ADVANCES = {" ":.217,"!":.285,'"':.38,"#":.5,$:.5,"%":.764,"&":.715,"'":.199,"(":.352,")":.352,"*":.405,"+":.577,",":.254,"-":.342,".":.254,"/":.327,0:.5,1:.5,2:.5,3:.5,4:.5,5:.5,6:.5,7:.5,8:.5,9:.5,":":.254,";":.254,"<":.548,"=":.577,">":.548,"?":.399,"@":.766,A:.706,B:.637,C:.676,D:.761,E:.581,F:.554,G:.727,H:.783,I:.378,J:.366,K:.703,L:.551,M:.888,N:.74,O:.766,P:.599,Q:.758,R:.659,S:.505,T:.641,U:.729,V:.711,W:.979,X:.693,Y:.655,Z:.619,"[":.351,"\\":.332,"]":.351,"^":.559,_:.5,"`":.4,a:.466,b:.534,c:.422,d:.547,e:.437,f:.331,g:.492,h:.55,i:.295,j:.288,k:.513,l:.278,m:.843,n:.566,o:.525,p:.547,q:.536,r:.404,s:.383,t:.339,u:.555,v:.465,w:.683,x:.482,y:.465,z:.445,"{":.357,"|":.27,"}":.357,"~":.577};

/* SPID-282 Phase D: per-font glyph advance tables for the seven faces Pratik
 * approved off the shop's own Fancy Product Designer font list, generated by
 * scratchpad/build_font_tables.py from the same 95-character set BB_MINION_ADVANCES
 * above covers. Its own 'Minion Pro Bold' row is character-for-character identical
 * to BB_MINION_ADVANCES (verified in node before this was pasted in), so nothing
 * that does not pass a font label changes behaviour - see bbMeasureTextPoints,
 * which keeps BB_MINION_ADVANCES as its default table rather than looking this
 * object up for the no-font-passed case. */
var BB_FONT_ADVANCES = {
  'Arial': { ' ': 0.278, '!': 0.278, '"': 0.355, '#': 0.556, '$': 0.556, '%': 0.889, '&': 0.667, '\'': 0.191, '(': 0.333, ')': 0.333, '*': 0.389, '+': 0.584, ',': 0.278, '-': 0.333, '.': 0.278, '/': 0.278, 0: 0.556, 1: 0.556, 2: 0.556, 3: 0.556, 4: 0.556, 5: 0.556, 6: 0.556, 7: 0.556, 8: 0.556, 9: 0.556, ':': 0.278, ';': 0.278, '<': 0.584, '=': 0.584, '>': 0.584, '?': 0.556, '@': 1.015, A: 0.667, B: 0.667, C: 0.722, D: 0.722, E: 0.667, F: 0.611, G: 0.778, H: 0.722, I: 0.278, J: 0.5, K: 0.667, L: 0.556, M: 0.833, N: 0.722, O: 0.778, P: 0.667, Q: 0.778, R: 0.722, S: 0.667, T: 0.611, U: 0.722, V: 0.667, W: 0.944, X: 0.667, Y: 0.667, Z: 0.611, '[': 0.278, '\\': 0.278, ']': 0.278, '^': 0.469, '_': 0.556, '`': 0.333, a: 0.556, b: 0.556, c: 0.5, d: 0.556, e: 0.556, f: 0.278, g: 0.556, h: 0.556, i: 0.222, j: 0.222, k: 0.5, l: 0.222, m: 0.833, n: 0.556, o: 0.556, p: 0.556, q: 0.556, r: 0.333, s: 0.5, t: 0.278, u: 0.556, v: 0.5, w: 0.722, x: 0.5, y: 0.5, z: 0.5, '{': 0.334, '|': 0.26, '}': 0.334, '~': 0.584 },
  'Bodoni Bold': { ' ': 0.26, '!': 0.305, '"': 0.227, '#': 0.607, '$': 0.573, '%': 0.769, '&': 0.751, '\'': 0.147, '(': 0.301, ')': 0.301, '*': 0.573, '+': 0.573, ',': 0.281, '-': 0.263, '.': 0.28, '/': 0.43, 0: 0.573, 1: 0.573, 2: 0.573, 3: 0.573, 4: 0.573, 5: 0.573, 6: 0.573, 7: 0.573, 8: 0.573, 9: 0.573, ':': 0.28, ';': 0.281, '<': 0.54, '=': 0.573, '>': 0.54, '?': 0.43, '@': 0.762, A: 0.706, B: 0.656, C: 0.682, D: 0.757, E: 0.688, F: 0.628, G: 0.756, H: 0.828, I: 0.374, J: 0.391, K: 0.741, L: 0.674, M: 0.872, N: 0.765, O: 0.737, P: 0.648, Q: 0.737, R: 0.719, S: 0.594, T: 0.672, U: 0.761, V: 0.684, W: 0.944, X: 0.753, Y: 0.635, Z: 0.65, '[': 0.301, '\\': 0.43, ']': 0.301, '^': 0.54, '_': 0.54, '`': 0.54, a: 0.528, b: 0.584, c: 0.456, d: 0.584, e: 0.48, f: 0.328, g: 0.487, h: 0.582, i: 0.298, j: 0.274, k: 0.527, l: 0.288, m: 0.855, n: 0.59, o: 0.533, p: 0.582, q: 0.582, r: 0.407, s: 0.414, t: 0.325, u: 0.583, v: 0.482, w: 0.701, x: 0.517, y: 0.478, z: 0.463, '{': 0.301, '|': 0.54, '}': 0.301, '~': 0.54 },
  'Futura Bold': { ' ': 0.22, '!': 0.275, '"': 0.314, '#': 0.769, '$': 0.44, '%': 0.819, '&': 0.545, '\'': 0.163, '(': 0.277, ')': 0.277, '*': 0.44, '+': 0.833, ',': 0.22, '-': 0.326, '.': 0.22, '/': 0.306, 0: 0.44, 1: 0.44, 2: 0.44, 3: 0.44, 4: 0.44, 5: 0.44, 6: 0.44, 7: 0.44, 8: 0.44, 9: 0.44, ':': 0.275, ';': 0.275, '<': 0.833, '=': 0.833, '>': 0.833, '?': 0.431, '@': 1, A: 0.465, B: 0.488, C: 0.455, D: 0.538, E: 0.396, F: 0.39, G: 0.556, H: 0.553, I: 0.27, J: 0.315, K: 0.476, L: 0.359, M: 0.667, N: 0.554, O: 0.565, P: 0.455, Q: 0.565, R: 0.459, S: 0.384, T: 0.389, U: 0.549, V: 0.486, W: 0.731, X: 0.422, Y: 0.391, Z: 0.428, '[': 0.277, '\\': 0.306, ']': 0.277, '^': 1, '_': 0.5, '`': 0.5, a: 0.453, b: 0.453, c: 0.318, d: 0.453, e: 0.428, f: 0.259, g: 0.453, h: 0.447, i: 0.236, j: 0.236, k: 0.419, l: 0.236, m: 0.656, n: 0.447, o: 0.444, p: 0.453, q: 0.453, r: 0.287, s: 0.335, t: 0.257, u: 0.441, v: 0.377, w: 0.579, x: 0.37, y: 0.37, z: 0.329, '{': 0.5, '|': 0.5, '}': 0.5, '~': 0.833 },
  'Garamond Bold': { ' ': 0.283, '!': 0.283, '"': 0.402, '#': 0.559, '$': 0.559, '%': 0.882, '&': 0.764, '\'': 0.26, '(': 0.402, ')': 0.402, '*': 0.339, '+': 0.559, ',': 0.283, '-': 0.299, '.': 0.283, '/': 0.441, 0: 0.559, 1: 0.559, 2: 0.559, 3: 0.559, 4: 0.559, 5: 0.559, 6: 0.559, 7: 0.559, 8: 0.559, 9: 0.559, ':': 0.283, ';': 0.283, '<': 0.559, '=': 0.559, '>': 0.559, '?': 0.417, '@': 0.717, A: 0.661, B: 0.638, C: 0.661, D: 0.764, E: 0.598, F: 0.559, G: 0.717, H: 0.78, I: 0.362, J: 0.402, K: 0.74, L: 0.543, M: 0.858, N: 0.717, O: 0.764, P: 0.598, Q: 0.764, R: 0.661, S: 0.52, T: 0.598, U: 0.701, V: 0.638, W: 0.937, X: 0.701, Y: 0.677, Z: 0.622, '[': 0.283, '\\': 0.543, ']': 0.283, '^': 0.559, '_': 0.504, '`': 0.402, a: 0.52, b: 0.598, c: 0.504, d: 0.598, e: 0.52, f: 0.362, g: 0.543, h: 0.661, i: 0.323, j: 0.299, k: 0.598, l: 0.323, m: 0.937, n: 0.661, o: 0.598, p: 0.638, q: 0.598, r: 0.457, s: 0.457, t: 0.339, u: 0.598, v: 0.543, w: 0.819, x: 0.622, y: 0.559, z: 0.48, '{': 0.283, '|': 0.559, '}': 0.283, '~': 0.559 },
  'Minion Pro Bold': { ' ': 0.217, '!': 0.285, '"': 0.38, '#': 0.5, '$': 0.5, '%': 0.764, '&': 0.715, '\'': 0.199, '(': 0.352, ')': 0.352, '*': 0.405, '+': 0.577, ',': 0.254, '-': 0.342, '.': 0.254, '/': 0.327, 0: 0.5, 1: 0.5, 2: 0.5, 3: 0.5, 4: 0.5, 5: 0.5, 6: 0.5, 7: 0.5, 8: 0.5, 9: 0.5, ':': 0.254, ';': 0.254, '<': 0.548, '=': 0.577, '>': 0.548, '?': 0.399, '@': 0.766, A: 0.706, B: 0.637, C: 0.676, D: 0.761, E: 0.581, F: 0.554, G: 0.727, H: 0.783, I: 0.378, J: 0.366, K: 0.703, L: 0.551, M: 0.888, N: 0.74, O: 0.766, P: 0.599, Q: 0.758, R: 0.659, S: 0.505, T: 0.641, U: 0.729, V: 0.711, W: 0.979, X: 0.693, Y: 0.655, Z: 0.619, '[': 0.351, '\\': 0.332, ']': 0.351, '^': 0.559, '_': 0.5, '`': 0.4, a: 0.466, b: 0.534, c: 0.422, d: 0.547, e: 0.437, f: 0.331, g: 0.492, h: 0.55, i: 0.295, j: 0.288, k: 0.513, l: 0.278, m: 0.843, n: 0.566, o: 0.525, p: 0.547, q: 0.536, r: 0.404, s: 0.383, t: 0.339, u: 0.555, v: 0.465, w: 0.683, x: 0.482, y: 0.465, z: 0.445, '{': 0.357, '|': 0.27, '}': 0.357, '~': 0.577 },
  'Rockwell': { ' ': 0.25, '!': 0.292, '"': 0.406, '#': 0.667, '$': 0.542, '%': 0.969, '&': 0.677, '\'': 0.177, '(': 0.375, ')': 0.375, '*': 0.521, '+': 0.667, ',': 0.292, '-': 0.354, '.': 0.292, '/': 0.438, 0: 0.542, 1: 0.542, 2: 0.542, 3: 0.542, 4: 0.542, 5: 0.542, 6: 0.542, 7: 0.542, 8: 0.542, 9: 0.542, ':': 0.292, ';': 0.292, '<': 0.667, '=': 0.667, '>': 0.667, '?': 0.469, '@': 1.01, A: 0.698, B: 0.583, C: 0.76, D: 0.729, E: 0.625, F: 0.552, G: 0.802, H: 0.708, I: 0.313, J: 0.313, K: 0.625, L: 0.521, M: 0.875, N: 0.719, O: 0.802, P: 0.573, Q: 0.802, R: 0.604, S: 0.531, T: 0.573, U: 0.677, V: 0.708, W: 1, X: 0.656, Y: 0.646, Z: 0.563, '[': 0.375, '\\': 0.438, ']': 0.375, '^': 0.469, '_': 0.5, '`': 0.333, a: 0.531, b: 0.646, c: 0.521, d: 0.625, e: 0.573, f: 0.292, g: 0.625, h: 0.573, i: 0.292, j: 0.292, k: 0.573, l: 0.292, m: 0.865, n: 0.573, o: 0.573, p: 0.625, q: 0.615, r: 0.417, s: 0.458, t: 0.302, u: 0.563, v: 0.542, w: 0.771, x: 0.552, y: 0.563, z: 0.448, '{': 0.333, '|': 0.527, '}': 0.333, '~': 0.583 },
  'Verdana': { ' ': 0.342, '!': 0.402, '"': 0.587, '#': 0.867, '$': 0.711, '%': 1.272, '&': 0.862, '\'': 0.332, '(': 0.543, ')': 0.543, '*': 0.711, '+': 0.867, ',': 0.361, '-': 0.48, '.': 0.361, '/': 0.689, 0: 0.711, 1: 0.711, 2: 0.711, 3: 0.711, 4: 0.711, 5: 0.711, 6: 0.711, 7: 0.711, 8: 0.711, 9: 0.711, ':': 0.402, ';': 0.402, '<': 0.867, '=': 0.867, '>': 0.867, '?': 0.617, '@': 0.964, A: 0.776, B: 0.762, C: 0.724, D: 0.83, E: 0.683, F: 0.65, G: 0.811, H: 0.837, I: 0.546, J: 0.555, K: 0.771, L: 0.637, M: 0.948, N: 0.847, O: 0.85, P: 0.733, Q: 0.85, R: 0.782, S: 0.71, T: 0.682, U: 0.812, V: 0.764, W: 1.128, X: 0.764, Y: 0.737, Z: 0.692, '[': 0.543, '\\': 0.689, ']': 0.543, '^': 0.867, '_': 0.711, '`': 0.711, a: 0.668, b: 0.699, c: 0.588, d: 0.699, e: 0.664, f: 0.422, g: 0.699, h: 0.712, i: 0.342, j: 0.403, k: 0.671, l: 0.342, m: 1.058, n: 0.712, o: 0.687, p: 0.699, q: 0.699, r: 0.497, s: 0.593, t: 0.456, u: 0.712, v: 0.65, w: 0.979, x: 0.669, y: 0.651, z: 0.597, '{': 0.711, '|': 0.543, '}': 0.711, '~': 0.867 },
};

// SPID-282 Phase D: the label a line renders at when nobody has picked a font -
// the same "Minion Pro Bold" row above, so bbMeasureTextPoints's fallback to
// BB_MINION_ADVANCES (not a lookup through this table) is what actually runs for
// it, and bbRenderReferenceText clears any font-family/weight override rather
// than looking BB_FONT_FACES up for it - see both functions below.
var BB_FONT_DEFAULT = 'Minion Pro Bold';

/* The CSS face each font label renders at. The four "... Bold" labels are the
 * actual Bold face (weight 700) - Fancy Product Designer's own bold toggle is
 * off by default, so a label like "Minion Pro Bold" there actually resolves to
 * the REGULAR face, but our badge text is genuinely bold, so BB_FONT_ADVANCES was
 * measured from the true Bold face for those four and the plain face for
 * Rockwell and Verdana. Keeping this table in step with which face
 * build_font_tables.py measured is what keeps the preview's printed width
 * honest - see the Phase D plan. Arial has no subset webfont; it is the system
 * font already on every visitor's device. */
var BB_FONT_FACES = {
  'Arial': { family: 'Arial, Helvetica, sans-serif', bold: false },
  // unavailable: withheld from the font picker (2026-09-22, Pratik). The vendor files
  // for these two are malformed - their glyf and hmtx tables are longer than the rest
  // of the font declares - and every browser rejects them, including when loaded
  // straight from the designer app's own CDN, so neither the preview nor the proof
  // could ever show the typeface a shopper picked. Offering a font that silently
  // prints as something else is worse than offering six. The rows themselves stay:
  // their measured widths are correct, so a saved design naming one still renders and
  // measures properly, and valid files make this a one-word change. See SPID-283.
  'Bodoni Bold': { family: '"BB Bodoni Bold", Georgia, serif', bold: true, unavailable: true },
  'Futura Bold': { family: '"BB Futura Bold", Arial, sans-serif', bold: true },
  'Garamond Bold': { family: '"BB Garamond Bold", Georgia, serif', bold: true, unavailable: true },
  'Minion Pro Bold': { family: '"BB Minion Pro Bold", "Minion Pro", "Times New Roman", serif', bold: true },
  'Rockwell': { family: '"BB Rockwell", Georgia, serif', bold: false },
  'Verdana': { family: '"BB Verdana", Verdana, sans-serif', bold: false }
};

// Whether a shopper may CHOOSE this font. Everything else about an unavailable font
// keeps working - it measures, renders and rides to the order exactly as before - so
// this is the only place the picker has to ask, and re-enabling one is a matter of
// dropping its flag above rather than editing the editor, the proof and the payload.
// Live v1 (Simon 2026-10-17): Minion Pro Bold only. The other fonts come back once
// free, print- and web-licensed replacements are chosen; add their labels here.
var BB_FONTS_ENABLED = ['Minion Pro Bold'];

function bbFontIsSelectable(label) {
  var face = BB_FONT_FACES[label];
  return !!face && !face.unavailable && BB_FONTS_ENABLED.indexOf(label) >= 0;
}

var BB_FONT_PROFILES = {
  H: { sizingWidth: 3.0694, titleInsetSide: 0.0658, titleHeight: 1,    maxFontSingle: 105, maxFontLine1: 45, maxFontLine2: 45, maxSumFont: 85 },
  V: { sizingWidth: 1.875,  titleInsetSide: 0.0585, titleHeight: 1.25, maxFontSingle: 105, maxFontLine1: 75, maxFontLine2: 75, maxSumFont: 120 }
};

function bbInches(value) { return value * BB_POINTS_PER_INCH; }

// Half-to-even at one decimal, matching the renderer's readout exactly.
function bbRoundTenth(value) {
  var scaled = value * 10, lower = Math.floor(scaled), fraction = scaled - lower;
  return Math.abs(fraction - 0.5) < 1e-10
    ? (lower % 2 === 0 ? lower : lower + 1) / 10
    : Math.round(scaled) / 10;
}

// SPID-282 Phase E: rounds a die-point value to hundredths for the order payload -
// the same math assets/bb-advanced.js's own bbAdvRoundPt uses, kept as its own
// copy here (not a shared export) because this file has to build a complete
// payload even on a page where bb-advanced.js never loads at all.
function bbDesignRoundPt(value) {
  return Math.round(value * 100) / 100;
}

function bbNormalizeLine(value) {
  return String(value == null ? '' : value).replace(/[\r\n\u00a0]+/g, ' ').trim();
}

// Width in points. Unknown characters fall back to 0.5em, as in the renderer.
//
// SPID-282 Phase D: fontLabel is optional and looks up BB_FONT_ADVANCES; leaving
// it out (every call site before Phase D, and every call this file still makes
// on behalf of a line nobody has picked a font for) uses BB_MINION_ADVANCES
// directly rather than a lookup through BB_FONT_ADVANCES['Minion Pro Bold'] - the
// two tables are verified identical, but reading the original var here is what
// makes "nothing that does not pass a font changes behaviour" actually true
// rather than merely true today.
//
// BB placement rules v1: a font the layout pack has is measured from the pack - the
// same 1/1000 em advances production writes into the PDF, for every character the
// font file has (accents included), so a moved/resized line and the automatic layout
// measure alike. The tables below only answer for a font the pack does not have
// (Bodoni Bold, Garamond Bold) or a page where the pack failed to load.
function bbMeasureTextPoints(text, fontSize, fontLabel) {
  var pack = bbLayoutPack();
  var packLabel = fontLabel || BB_FONT_DEFAULT;
  if (pack && pack.font_labels.indexOf(packLabel) !== -1) {
    return pack.font(packLabel).pdf_width_in(String(text || ''), fontSize) * BB_POINTS_PER_INCH;
  }
  var table = (fontLabel && BB_FONT_ADVANCES[fontLabel]) || BB_MINION_ADVANCES;
  var total = 0, chars = String(text || '').split('');
  for (var i = 0; i < chars.length; i++) {
    var advance = table[chars[i]];
    total += (advance == null ? 0.5 : advance);
  }
  return total * fontSize;
}

/* SPID-282: the real vertical ink extent of every printable ASCII glyph in each
 * designer font, in thousandths of an em (yMin,yMax per glyph, U+0020..U+007E in
 * order). Generated from the font files themselves by
 * E:/Projects/SPID/tools/bb-designer/build_font_extents.py - regenerate, never
 * hand-edit. `fallback` is the furthest the font declares any of its glyphs reach,
 * used for any character not in the table (accents, curly quotes, symbols).
 *
 * Why it exists: the no-overlap rule used to treat every line as 0.70em tall and
 * 0.21em deep. The real fonts go further - Futura Bold's "f" reaches 0.766em,
 * Minion Pro Bold's "y" drops 0.245em - so two lines the rule called clear by
 * 1.4pt actually printed into each other by 1.1pt (Odin's QA, 2026-10-06:
 * "the two lines sometimes overlap ... when the bottom line has ascenders and
 * the top line has descenders"). */
var BB_FONT_EXTENTS = {
  "Arial": { fallback: [-212, 905], glyphs: "0,0;0,716;462,716;-12,728;-103,782;-26,728;-17,728;462,716;-210,728;-210,728;423,728;116,589;-142,100;215,303;0,100;-12,728;-12,719;0,719;0,719;-13,719;0,716;-12,706;-12,719;0,707;-12,719;-12,719;0,519;-142,519;110,595;204,503;110,595;0,728;-210,729;0,716;0,716;-12,728;0,716;0,716;0,716;-12,728;0,716;0,716;-12,716;0,716;0,716;0,716;0,716;-12,729;0,716;-56,729;0,716;-12,728;0,716;-12,716;0,716;0,716;0,716;0,716;0,716;-199,716;-12,728;-199,716;337,728;-199,-135;583,720;-12,530;-12,716;-12,530;-12,716;-12,530;0,728;-210,530;0,716;0,716;-210,716;0,716;0,716;0,530;0,530;-12,530;-199,530;-199,530;0,530;-12,530;-7,700;-12,519;0,519;0,519;0,519;-210,519;0,519;-210,728;-210,728;-210,728;272,432" },
  "Futura Bold": { fallback: [-236, 963], glyphs: "0,0;-9,713;429,701;-3,713;-89,791;-9,722;-5,699;429,701;-217,760;-217,760;367,713;0,596;-137,159;162,317;-6,167;-93,722;-9,722;0,713;0,722;-10,722;0,713;-9,713;-10,713;0,713;-9,722;0,722;-6,454;-137,454;23,574;157,440;23,574;-9,722;-147,719;0,713;0,713;-9,722;0,713;0,713;0,713;-9,722;0,713;0,713;-10,713;1,713;0,713;0,713;0,713;-9,722;0,713;-52,722;0,713;-9,722;0,713;-9,713;0,713;0,713;0,713;0,713;0,713;-194,736;-93,722;-194,736;439,713;-236,-152;544,725;-9,484;-10,757;-9,488;-10,757;-9,488;0,766;-221,484;0,757;0,684;-193,684;0,757;0,757;0,484;0,484;-9,488;-193,484;-193,484;0,488;-9,488;0,617;-9,479;0,479;0,479;0,479;-193,479;0,479;-202,721;-236,764;-202,721;212,383" },
  "Minion Pro Bold": { fallback: [-360, 1024], glyphs: "0,0;-12,665;422,708;15,614;-71,689;-38,650;-14,663;422,708;-172,714;-172,714;306,665;0,486;-133,161;193,265;-12,140;-45,665;-12,635;0,635;0,635;-12,635;0,629;-12,623;-12,641;-12,623;-12,635;-15,635;-12,419;-133,419;0,506;130,378;0,506;-12,665;-100,577;0,665;0,650;-15,665;0,650;0,650;0,650;-15,665;0,650;0,650;-163,650;0,650;0,650;0,650;-10,650;-15,664;0,650;-168,665;-9,650;-15,665;0,675;-15,650;-11,650;-11,650;0,650;0,650;0,675;-162,704;-45,665;-162,704;213,623;-125,-75;489,678;-12,454;-12,703;-12,454;-13,703;-12,454;0,703;-244,454;0,703;0,657;-244,657;-2,703;0,703;0,456;0,456;-12,454;-230,464;-230,454;0,455;-12,454;-12,593;-12,449;-11,442;-11,442;0,442;-245,442;0,464;-168,710;-250,750;-168,710;182,322" },
  "Rockwell": { fallback: [-229, 946], glyphs: "0,0;0,679;405,690;-9,688;-85,705;-11,690;-11,690;404,690;-217,691;-217,691;260,690;72,607;-135,120;188,281;0,120;-11,693;-11,690;0,678;0,690;-11,690;0,685;-11,678;-11,690;0,678;-11,690;-11,690;0,473;-135,473;81,599;187,493;81,599;0,690;-223,716;0,679;0,679;-11,690;0,679;0,679;0,679;-11,690;0,679;0,679;-213,679;0,679;0,679;-7,679;-7,679;-11,690;0,679;-131,690;0,679;-11,690;0,679;-11,679;-7,679;-7,679;0,679;0,679;0,679;-204,679;-11,693;-204,679;288,679;-125,-75;535,690;-8,486;-8,679;-14,486;-8,679;-14,486;0,693;-223,480;0,679;0,689;-208,689;0,679;0,679;0,480;0,480;-14,486;-212,480;-212,480;0,480;-8,480;-8,630;-8,473;-8,473;-8,473;0,473;-212,473;0,473;-212,679;-223,693;-212,679;263,417" },
  "Verdana": { fallback: [-210, 1005], glyphs: "0,0;0,727;472,760;0,727;-176,770;-15,741;-15,742;472,760;-201,760;-201,760;261,760;0,646;-181,192;253,394;0,192;-158,760;-15,742;0,729;0,741;-15,742;-2,727;-15,727;-15,741;0,727;-17,744;-14,742;0,548;-181,548;-3,650;129,517;-3,650;0,742;-112,741;0,727;0,727;-14,741;0,727;0,727;0,727;-15,741;0,727;0,727;-11,727;0,727;0,727;0,727;0,727;-15,742;0,727;-208,742;0,727;-13,740;0,727;-15,727;0,727;0,727;0,727;0,727;0,727;-191,760;-158,760;-191,760;312,727;-171,-68;626,818;-15,564;-11,760;-15,563;-15,760;-15,565;0,765;-207,563;0,760;0,760;-207,760;0,760;0,760;0,563;0,563;-17,565;-201,563;-201,563;0,550;-15,563;-11,705;-15,548;0,548;0,548;0,548;-201,548;0,548;-191,760;-191,760;-191,760;159,492" }
};

var bbFontExtentCache = {};

// The ink of `text` set in `fontLabel`, in ems: how far it rises above the baseline
// (up) and how far it drops below (down). Spaces carry no ink. A font with no table
// (none today; the two withheld fonts never reach here) measures as the default
// font's fallback, the most generous answer available. Empty text has no ink.
//
// BB placement rules v1: read from the layout pack (production's own ink boxes, every
// character the font file has) whenever the pack has the font; BB_FONT_EXTENTS above is
// only the fallback for a font it does not have or a page where it failed to load.
function bbTextInkExtentEm(text, fontLabel) {
  var pack = bbLayoutPack();
  var packLabel = fontLabel || BB_FONT_DEFAULT;
  if (pack && pack.font_labels.indexOf(packLabel) !== -1) {
    var inkY = pack.font(packLabel).ink_y_em(String(text || ''));
    return { up: Math.max(0, inkY[0]), down: Math.max(0, -inkY[1]) };
  }
  var label = (fontLabel && BB_FONT_EXTENTS[fontLabel]) ? fontLabel : BB_FONT_DEFAULT;
  var entry = BB_FONT_EXTENTS[label];
  var glyphs = bbFontExtentCache[label];
  if (!glyphs) {
    glyphs = bbFontExtentCache[label] = entry.glyphs.split(';').map(function (pair) {
      if (pair === '_') return null;
      var parts = pair.split(',');
      return [Number(parts[0]) / 1000, Number(parts[1]) / 1000];
    });
  }
  var useFallback = !(fontLabel && BB_FONT_EXTENTS[fontLabel]) && !!fontLabel;
  var up = 0, down = 0, chars = String(text || '').split('');
  for (var i = 0; i < chars.length; i++) {
    var code = chars[i].charCodeAt(0);
    var ext = (!useFallback && code >= 0x20 && code <= 0x7E) ? glyphs[code - 0x20] : null;
    if (!ext) ext = [entry.fallback[0] / 1000, entry.fallback[1] / 1000];
    if (ext[1] > up) up = ext[1];
    if (-ext[0] > down) down = -ext[0];
  }
  return { up: up, down: down };
}

// Badge Bottoms only (BB placement rules v1): the Badge Buddy dies are laid out by
// production's engine (bbAutoLayout below). Badge Bottoms have their own centring rule,
// still open with Simon, so they keep this sizing and bbBottomsSizing's placement.
//
// SPID-282 Phase D: fonts is optional - {line1, line2}, each a BB_FONT_ADVANCES
// label or falsy for the default. Called with three arguments (fonts omitted) it
// behaves exactly as before this phase - compare_sizing.js calls it that way and
// must still print 0 mismatches - because every measurement below passes fonts.line1
// / fonts.line2 straight through to bbMeasureTextPoints, which itself falls back to
// BB_MINION_ADVANCES for an undefined label.
function bbCalculateFontSizes(orientation, rawLine1, rawLine2, fonts) {
  var profile = BB_FONT_PROFILES[orientation];
  if (!profile) return null;

  var line1 = bbNormalizeLine(rawLine1), line2 = bbNormalizeLine(rawLine2);
  var font1 = fonts && fonts.line1, font2 = fonts && fonts.line2;
  var maxTextWidth = bbInches(profile.sizingWidth - 2 * profile.titleInsetSide - 0.1);
  var maxTextHeight = bbInches(profile.titleHeight - 0.1);

  function maxFontSizeForWidth(text, font) {
    var widthAtReference = bbMeasureTextPoints(text, BB_REFERENCE_FONT_SIZE, font);
    return widthAtReference <= 0
      ? profile.maxFontSingle
      : BB_REFERENCE_FONT_SIZE * maxTextWidth / widthAtReference;
  }

  var fontSize1 = 0, fontSize2 = 0, templateCase = 'A';

  if (line1 && !line2) {
    fontSize1 = Math.max(Math.min(maxFontSizeForWidth(line1, font1), profile.maxFontSingle, maxTextHeight), 0.5);
    templateCase = 'A';
  } else if (line2 && !line1) {
    fontSize2 = Math.max(Math.min(maxFontSizeForWidth(line2, font2), profile.maxFontSingle, maxTextHeight), 0.5);
    templateCase = 'B';
  } else if (line1 && line2) {
    var maxFont1Width = maxFontSizeForWidth(line1, font1), maxFont2Width = maxFontSizeForWidth(line2, font2);
    var maxFontHeight = maxTextHeight * 0.52;
    fontSize1 = Math.min(maxFont1Width, maxFontHeight, profile.maxFontLine1);
    fontSize2 = Math.min(maxFont2Width, maxFontHeight, profile.maxFontLine2);
    // Two substantial lines are set at a single size; short ones are allowed to differ.
    if (line1.length > 3 && line2.length > 3) {
      var matched = Math.min(fontSize1, fontSize2);
      fontSize1 = matched; fontSize2 = matched;
    }
    if (fontSize1 + fontSize2 > profile.maxSumFont) {
      var ratio = profile.maxSumFont / (fontSize1 + fontSize2);
      fontSize1 *= ratio; fontSize2 *= ratio;
    }
    fontSize1 = Math.max(fontSize1, 0.5); fontSize2 = Math.max(fontSize2, 0.5);
    templateCase = maxFont1Width < maxFont2Width ? 'D' : 'E';
  }

  return {
    line1: line1, line2: line2,
    fontSize1: bbRoundTenth(fontSize1), fontSize2: bbRoundTenth(fontSize2),
    templateCase: templateCase,
    heightCap: maxTextHeight
  };
}

/* Badge Buddy title layout: BB placement rules v1 (Simon and Odin, 2026-10-09/10).
 *
 * On the six Badge Buddy dies the font size and position of the title come from
 * production's own layout engine, assets/bb-layout-engine.js (window.BBLayout - a
 * line-for-line port of production's scripts/bb_layout_engine.py that passes
 * production's golden file exactly), reading production's layout pack,
 * assets/bb-layout-pack.js (window.BB_LAYOUT_PACK - glyph metrics per font, every rule
 * constant, the approved acronym list) with the approved acronyms from the shop
 * metafield custom.bb_approved_acronyms (window.BB_APPROVED_ACRONYMS, the list
 * production uses; the pack's list when absent or invalid). Nothing here measures text in the browser or
 * adds a placement rule of its own: the engine decides, this file converts its answer
 * to die points and draws it.
 *
 * - Standard bar = the engine's 'box' style; Full-width bar and Whole badge = 'bleed'.
 * - H/V and STD/XL come from the die key. The engine lays XL out exactly like STD.
 * - Text goes in as production receives it: the field value with only the leading and
 *   trailing whitespace removed (BBLayout.productionStrip, Python's str.strip()).
 * - A font per line (Simon 2026-10-10): Line 1 in its picker's font, Line 2 in its own
 *   (the engine's font / font2). Each line is sized, measured and placed in its own font;
 *   two lines production sets at one size follow the pack rule
 *   rules.mixed_font_equal_size when their fonts differ.
 * - Units: the engine answers in inches, x from the die centre, y UP from the bottom
 *   cut. The stage SVG is die points (72 per inch), y DOWN from the die top, and its
 *   bottom edge is the cut, so x_pt = dieWidth/2 + x_in * 72, y_pt = dieHeight - y_in * 72.
 * - Every character is drawn at its own x (BBLayout.glyphOffsets: the pack's advances,
 *   the widths production writes into the PDF), with kerning and ligatures off, so the
 *   browser's own text shaping cannot move a letter.
 *
 * scripts/bb-layout/check-designer.mjs runs production's golden cases through
 * bbAutoLayout below (the function bbRenderReferenceText draws from) and checks the
 * die numbers against the pack.
 */
var bbLayoutPackCache = null;

function bbLayoutPack() {
  if (bbLayoutPackCache) return bbLayoutPackCache;
  var engine = window.BBLayout;
  if (!engine || !window.BB_LAYOUT_PACK) return null;
  try {
    // Approved acronyms: the shop metafield custom.bb_approved_acronyms (snippets/
    // bb-approved-acronyms.liquid), the list production uses; absent or invalid -> the
    // pack's own list (the engine validates it exactly as production does).
    bbLayoutPackCache = engine.loadPack(window.BB_LAYOUT_PACK, {
      source: 'assets/bb-layout-pack.js',
      acronyms: window.BB_APPROVED_ACRONYMS
    });
  } catch (e) {
    bbLayoutPackCache = null;
  }
  return bbLayoutPackCache;
}

function bbLayoutHasFont(label) {
  var pack = bbLayoutPack();
  return !!(pack && label && pack.font_labels.indexOf(label) !== -1);
}

// Production trims each line once when the order comes in, and nothing else.
function bbLayoutStrip(text) {
  var value = text == null ? '' : String(text);
  return window.BBLayout ? window.BBLayout.productionStrip(value) : value.trim();
}

// The font each line is laid out and drawn in: its picker's font, or the default for a
// line nobody picked a font for (or a font the pack does not have). Each line is
// independent (Simon 2026-10-10). A single label sets both lines.
function bbLayoutLineFonts(lineFonts) {
  if (typeof lineFonts === 'string') lineFonts = { line1: lineFonts, line2: lineFonts };
  function pick(label) {
    return bbLayoutHasFont(label) ? label : BB_FONT_DEFAULT;
  }
  return { line1: pick(lineFonts && lineFonts.line1), line2: pick(lineFonts && lineFonts.line2) };
}

function bbLayoutStyleForBar(barStyle, key) {
  if (bbIsBottomDie(key)) return 'bottom';
  return (barStyle === 'full' || barStyle === 'whole') ? 'bleed' : 'box';
}

// Where each character of a line set at (font, fontSize) with its left end at x (die
// points) is drawn, die points. Null for a font the pack does not have.
function bbLayoutGlyphX(font, text, fontSize, x) {
  var pack = bbLayoutPack();
  if (!pack || !bbLayoutHasFont(font) || !text) return null;
  return window.BBLayout.glyphOffsets(pack, font, text, fontSize).map(function (g) {
    return x + g.dx_in * BB_POINTS_PER_INCH;
  });
}

// The automatic layout of one title on one die, in the stage's die points. placements
// keep the shape the editor and the order payload already read ({text, fontSize, width,
// x, baselineY}, top line first, a lone line at index 0) plus glyphX and font (that
// line's own font). lineFonts: {line1, line2} labels (or one label for both). Null when
// the engine or the pack did not load.
// "Make it bigger" (Simon 2026-10-10): the shopper's choice per side. It only applies
// while the engine offers it for the text on show (enlargeOption: a line grows by at
// least 10% with the two lines sized on their own, as for an approved acronym, and
// no line shrinks); production sizes the order the same way from properties[Enlarge text].
var bbEnlargeWanted = { front: false, back: false };

function bbAutoLayout(key, geometry, rawLine1, rawLine2, lineFonts, barStyle, side) {
  var pack = bbLayoutPack();
  if (!pack || !geometry || !BB_DIE[key]) return null;
  var line1 = bbLayoutStrip(rawLine1), line2 = bbLayoutStrip(rawLine2);
  var fonts = bbLayoutLineFonts(lineFonts);
  var opts = {
    font: fonts.line1,
    font2: fonts.line2,
    orientation: key.charAt(0),
    sizeClass: key.split('-')[1],
    style: bbLayoutStyleForBar(barStyle, key),
    line1: line1,
    line2: line2
  };
  var result = window.BBLayout.layoutTitle(pack, opts);
  var enlarge = { offered: false, applied: false, text: '', field: 0 };
  // No "Make bigger" on Badge Bottoms: their rule already sizes each title to the strip.
  if (!bbIsBottomDie(key) && typeof window.BBLayout.enlargeOption === 'function') {
    var offer = window.BBLayout.enlargeOption(pack, opts);
    if (offer.offered) {
      var best = 0, bestRatio = 0;
      offer.layout.lines.forEach(function (ln, i) {
        var ratio = ln.font_size / result.lines[i].font_size;
        if (ratio > bestRatio) { bestRatio = ratio; best = i; }
      });
      enlarge.offered = true;
      enlarge.text = offer.layout.lines[best].text;
      enlarge.field = offer.layout.lines[best].field;
      if (bbEnlargeWanted[side === 'back' ? 'back' : 'front']) {
        enlarge.applied = true;
        result = offer.layout;
      }
    }
  }

  var centreX = geometry.dieWidth / 2, cutY = geometry.dieHeight;
  var placements = result.lines.map(function (ln) {
    return {
      text: ln.text,
      fontSize: ln.font_size,
      width: ln.width_in * BB_POINTS_PER_INCH,
      x: centreX + ln.x_in * BB_POINTS_PER_INCH,
      baselineY: cutY - ln.baseline_from_cut_in * BB_POINTS_PER_INCH,
      font: ln.font,
      // Letters this line's font does not have (the order is blocked until they go).
      missing: ln.missing_glyphs || [],
      glyphX: window.BBLayout.glyphOffsets(pack, ln.font, ln.text, ln.font_size).map(function (g) {
        return centreX + (ln.x_in + g.dx_in) * BB_POINTS_PER_INCH;
      })
    };
  });

  var both = !!(line1 && line2);
  return {
    sizing: {
      line1: line1,
      line2: line2,
      fontSize1: line1 ? placements[0].fontSize : 0,
      fontSize2: line2 ? placements[both ? 1 : 0].fontSize : 0,
      font: fonts.line1,
      fonts: fonts,
      style: result.style,
      enlarge: enlarge.applied,
      layout: result
    },
    placements: placements,
    layout: result,
    enlarge: enlarge
  };
}

// The "Make bigger" box in the label row of the line that would grow, and the order
// property it drives.
function bbEnlargeRefresh(side, enlarge) {
  var offered = !!(enlarge && enlarge.offered);
  var shown = window.bbCurrentSide === 'back' && window.bbBackMode === 'different' ? 'back' : 'front';
  if (side === shown) {
    Array.prototype.forEach.call(document.querySelectorAll('[data-bb-enlarge]'), function (box) {
      box.hidden = !(offered && String(enlarge.field) === box.getAttribute('data-bb-enlarge'));
      var input = box.querySelector('[data-bb-enlarge-input]');
      if (input) input.checked = !!bbEnlargeWanted[side];
    });
  }
  var applied = !!(enlarge && enlarge.applied);
  var field = side === 'back' ? bbBackField('properties[Back Enlarge text]') : document.getElementById('bb_option_enlarge');
  if (field && field.tagName) {
    var on = applied && (side !== 'back' || window.bbBackMode === 'different');
    field.value = on ? 'Yes' : '';
    field.disabled = !on;
  }
}

document.addEventListener('change', function (e) {
  var input = e.target;
  if (!input || !input.matches || !input.matches('[data-bb-enlarge-input]')) return;
  var side = window.bbCurrentSide === 'back' && window.bbBackMode === 'different' ? 'back' : 'front';
  bbEnlargeWanted[side] = !!input.checked;
  bbRenderReferenceText();
});

/* What stops a side being ordered (Simon, 2026-10-10), from the engine's own verdict:
 * - below_min_font: a line would print under production's minimum (15 pt; 10 pt for a
 *   line beside an approved acronym such as RN). Production holds these orders, so the
 *   shopper is asked to shorten the text.
 * - missing_glyphs: a character the chosen font does not have. Until the full font
 *   files arrive the designer refuses it and suggests a font that has it.
 * The text stays in the field either way, so switching to a font that has the letter
 * clears the block without retyping. Badge Bottoms never set these. */
var bbLayoutSideIssues = { front: null, back: null };

function bbLayoutIssuesFor(auto) {
  if (!auto) return { unavailable: true, belowMin: false, belowLines: [], missing: [], missingLines: [], font: null, twoLines: false };
  var result = auto.layout;
  var belowMin = result.review_reasons.some(function (r) { return r.indexOf('below_min_font') === 0; });
  return {
    unavailable: false,
    belowMin: belowMin,
    belowLines: belowMin ? bbLayoutLinesBelowMin(auto) : [],
    missing: result.missing_glyphs.slice(),
    // Per line (the engine reports each line's missing letters in that line's font):
    // {line: 1 | 2 (the field), font, chars}.
    missingLines: result.lines.filter(function (ln) { return ln.missing_glyphs.length > 0; }).map(function (ln) {
      return { line: ln.field, font: ln.font, chars: ln.missing_glyphs.slice() };
    }),
    font: auto.sizing.font,
    twoLines: result.lines.length === 2
  };
}

// Which field (1 = top line, 2 = second line) is under the minimum - wording only; the
// decision itself is the engine's needs_review above.
function bbLayoutLinesBelowMin(auto) {
  var pack = bbLayoutPack(), lines = auto.layout.lines, out = [];
  lines.forEach(function (ln, i) {
    var other = lines.length === 2 ? lines[1 - i].text : '';
    var floor = (other && pack.is_approved_acronym(other) && !pack.is_approved_acronym(ln.text))
      ? pack.min_font_pt_with_acronym : pack.min_font_pt;
    if (ln.font_size < floor - 1e-9) out.push(bbDesignPlacementLine(auto.sizing, i));
  });
  return out;
}

// askBlank is the one soft block: a front with nothing typed is held for one Add to cart
// press (see the sample text below). It says nothing and does not grey the button, so
// bbLayoutOrderBlocked(true) leaves it out for the button styling only.
function bbLayoutIsBlocking(issues, hardOnly) {
  return !!issues && (issues.unavailable || issues.placeholder || issues.belowMin || issues.missing.length > 0 ||
    (!hardOnly && !!issues.askBlank));
}

// Live v1 (Simon 2026-10-17, "A"): the top line starts as the example text YOUR TEXT.
// A side still showing it on either line cannot be ordered, so nobody receives a badge
// that says YOUR TEXT; clearing the line (prints nothing) or typing a title clears it.
var BB_PLACEHOLDER_RE = /^\s*your\s+text\s*$/i;
function bbLayoutWithPlaceholder(issues, line1, line2) {
  issues.placeholder = BB_PLACEHOLDER_RE.test(line1 || '') || BB_PLACEHOLDER_RE.test(line2 || '');
  return issues;
}

// Sample text (Patrick, 2026-10-18): on the custom templates YOUR TEXT is the top line's
// gray placeholder, not its value, so nothing warns before the shopper has typed. While
// both lines of the front are empty the badge shows the sample faded; it is never part
// of the order. Merged with the live rule above (Rohan Joshi, from Patrick Barnhill's
// 4da97edb and Odin's 5793c8d6): the sample is only ever a preview, the literal text
// YOUR TEXT (typed, or left as a value) is still blocked by BB_PLACEHOLDER_RE, and a
// blank front is held by the same gate (issues.askBlank in bbLayoutOrderBlocked) until
// the shopper has been asked once (bbBlankAsked). The first Add to cart press is blocked
// like any other issue, drops the sample so the preview shows the blank badge and shows
// the note; the second passes. Typing anything puts the shopper back to the start.
window.bbBlankAsked = window.bbBlankAsked || false;
function bbFrontIsEmpty() {
  return !bbLayoutStrip(badgeText).trim() && !bbLayoutStrip(badgeText2).trim();
}
// Sample text of the front's top line, '' when this page has none (stock designers).
function bbSampleAttr() {
  var field = document.getElementById('bb-custom-line-1');
  return (field && field.getAttribute('data-bb-sample')) || '';
}
// The blank front still waiting to be asked about.
function bbBlankNeedsAsk(side) {
  return side === 'front' && !window.bbBlankAsked && !!bbSampleAttr() && bbFrontIsEmpty();
}
// The faded sample shown in the preview: exactly while the front waits to be asked.
function bbSampleTextFor(side) {
  return window.bbCurrentSide !== 'back' && bbBlankNeedsAsk(side) ? bbSampleAttr() : '';
}

// Selectable fonts that have every one of `chars`, the default font first.
function bbLayoutFontsWith(chars, exceptFont) {
  var pack = bbLayoutPack();
  if (!pack) return [];
  return Object.keys(BB_FONT_ADVANCES).filter(function (label) {
    if (label === exceptFont || !bbFontIsSelectable(label) || !bbLayoutHasFont(label)) return false;
    var metrics = pack.font(label);
    return chars.every(function (ch) { return metrics.has(ch); });
  }).sort(function (a, b) {
    return (a === BB_FONT_DEFAULT ? 0 : 1) - (b === BB_FONT_DEFAULT ? 0 : 1);
  });
}

function bbLayoutJoin(items, word) {
  if (items.length < 2) return items.join('');
  return items.slice(0, -1).join(', ') + ' ' + word + ' ' + items[items.length - 1];
}

function bbLayoutMessage(issues) {
  if (!bbLayoutIsBlocking(issues)) return '';
  if (issues.unavailable) {
    return 'The badge preview did not load, so this text cannot be checked for printing. Please refresh the page.';
  }
  var parts = [];
  if (issues.placeholder) {
    parts.push('Type your own text in place of "YOUR TEXT", or clear the line and press Add to cart twice to order it blank.');
  }
  var missingLines = issues.missingLines || [];
  // "Same font on both lines" ticked: one picker sets both, so one message about the
  // font with every missing letter of both lines.
  var sameFont = typeof window.bbAdvSameFont === 'function' ? window.bbAdvSameFont() : false;
  if (sameFont && missingLines.length > 1 && missingLines[0].font === missingLines[1].font) {
    var allChars = missingLines[0].chars.slice();
    missingLines[1].chars.forEach(function (ch) { if (allChars.indexOf(ch) < 0) allChars.push(ch); });
    missingLines = [{ line: 0, font: missingLines[0].font, chars: allChars }];
  }
  // A letter the line's font lacks: name the line and its font, and suggest the fonts
  // that have every missing letter of that line.
  missingLines.forEach(function (m) {
    // Invisible joiners and emoji variation selectors are not worth naming.
    var visible = m.chars.filter(function (ch) { return !/^[\uFE00-\uFE0F\u200B-\u200D\u2060]$/.test(ch); });
    m = { line: m.line, font: m.font, chars: visible.length ? visible : m.chars };
    var many = m.chars.length > 1;
    var where = m.line === 2 ? 'second line' : 'top line';
    var msg = sameFont
      ? 'This font (' + m.font + ") doesn't support " + bbLayoutJoin(m.chars, 'or') + '.'
      : 'The ' + where + "'s font (" + m.font + ") doesn't support " + bbLayoutJoin(m.chars, 'or') + '.';
    var others = bbLayoutFontsWith(m.chars, m.font);
    if (others.length) msg += ' Try ' + bbLayoutJoin(others, 'or') + (sameFont ? '.' : ' for the ' + where + '.');
    else msg += ' None of our fonts can print ' + (many ? 'these characters' : 'it') + ' yet. Please remove or replace ' + (many ? 'them' : 'it') + '.';
    parts.push(msg);
  });
  if (issues.belowMin) {
    var lines = issues.belowLines;
    var subject = lines.length === 2 ? 'Both lines are'
      : lines[0] === 1 && issues.twoLines ? 'The top line is'
      : lines[0] === 2 && issues.twoLines ? 'The second line is'
      : 'This text is';
    parts.push(subject + ' too long to print clearly. Please shorten ' + (lines.length === 2 ? 'them' : 'it') +
      ' (for example, use an abbreviation).');
  }
  return parts.join(' ');
}

function bbLayoutCountedSides() {
  return window.bbBackMode === 'different' ? ['front', 'back'] : ['front'];
}

function bbLayoutOrderBlocked(hardOnly) {
  return bbLayoutCountedSides().some(function (side) { return bbLayoutIsBlocking(bbLayoutSideIssues[side], hardOnly); });
}

// The note next to the fields and the Add to cart button, from bbLayoutSideIssues.
// The note speaks about the side on show; a problem on the other side of a two-sided
// order is named as such.
function bbLayoutRefreshGate(attempted) {
  var shown = window.bbCurrentSide === 'back' && window.bbBackMode === 'different' ? 'back' : 'front';
  var messages = [];
  bbLayoutCountedSides().forEach(function (side) {
    var text = bbLayoutMessage(bbLayoutSideIssues[side]);
    if (!text) return;
    if (side === shown) messages.unshift(text);
    else messages.push('The ' + side + ' of the badge needs a change too: ' + text);
  });
  if (window.bbBlankAsked && window.bbCurrentSide !== 'back' && bbFrontIsEmpty() && bbSampleAttr()) {
    messages.unshift('Type your text above, or press Add to cart again to order it blank.');
  }

  var note = document.querySelector('[data-bb-layout-note]');
  if (note) {
    var next = messages.join(' ');
    if (note.textContent !== next) note.textContent = next;
    note.hidden = !next;
    if (attempted && next && typeof note.scrollIntoView === 'function') {
      note.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }

  var blocked = bbLayoutOrderBlocked(true);
  var buttons = document.querySelectorAll('form[data-type="add-to-cart-form"] .product-form__submit');
  Array.prototype.forEach.call(buttons, function (button) {
    var ours = button.hasAttribute('data-bb-layout-blocked');
    if (blocked && !ours) {
      button.setAttribute('data-bb-layout-blocked', '');
      button.classList.add('bb-atc-layout-blocked');
      if (button.getAttribute('aria-disabled') !== 'true') {
        button.setAttribute('aria-disabled', 'true');
        button.setAttribute('data-bb-layout-set-aria', '');
      }
    } else if (!blocked && ours) {
      button.removeAttribute('data-bb-layout-blocked');
      button.classList.remove('bb-atc-layout-blocked');
      if (button.hasAttribute('data-bb-layout-set-aria')) {
        button.removeAttribute('data-bb-layout-set-aria');
        if (!button.disabled) button.removeAttribute('aria-disabled');
      }
    }
  });
}

// Add to cart gate. Capture on document, registered before the proof gate further down
// (and so before product-form.js's own submit handler), so a blocked order never starts
// a proof render or a /cart/add request. bb-advanced.js's window-level capture handler
// has already brought a shopper on the back to the front by the time this runs.
document.addEventListener('submit', function (e) {
  var form = e.target;
  if (!form || typeof form.getAttribute !== 'function' || form.getAttribute('data-type') !== 'add-to-cart-form') return;
  var designInput = document.getElementById('bb_option_design');
  if (!designInput || !form.contains(designInput)) return;
  if (!bbLayoutOrderBlocked()) return;
  e.preventDefault();
  e.stopImmediatePropagation();
  // A blank front is asked about once: this press was the question, the next is the answer.
  if (bbLayoutSideIssues.front && bbLayoutSideIssues.front.askBlank) {
    window.bbBlankAsked = true;
    bbRenderReferenceText();
  }
  bbLayoutRefreshGate(true);
}, true);

// The sizing behind what is currently drawn ({line1, line2, fontSize1, fontSize2, ...};
// on the Badge Buddy dies also font, style and the engine's layout). The editor reads
// line1/line2 off it. Null whenever the textfill path is in use.
var bbLastSizing = null;

/* Resolves the selected colour to a strip fill/stroke and a title fill/outline.
 *
 * The colour hexes already exist as `.product-variant-color-<slug> .selectedColor
 * {background-color; color}` rules in custom-bb.css - the same table the old photo
 * plate read. Reading getComputedStyle keeps that the one source of colour instead of
 * duplicating ~40 hexes here; `swatch` is a visually hidden element kept only so that
 * lookup has something to resolve against (see bb-stage-svg.liquid).
 *
 * Rainbow is a shop colour option with no equivalent in the renderer's own COLORS
 * table (which has no rainbow entry, only Pride) - its CSS is a `background-image`
 * gradient, which getComputedStyle cannot hand back as a fill, so it is special-cased
 * to the SVG gradient defined alongside the strip and given the renderer's Pride-style
 * outline so the title text stays legible over every band. */
function bbResolveStripColor(swatch, dieKey) {
  var container = document.querySelector('.product') || document.body;
  var isRainbow = /(^|\s)product-variant-color-rainbow(\s|$)/.test(container.className || '');
  if (isRainbow) {
    // No black border around the rainbow bar (Patrick, 2026-10-05) - it doesn't print
    // one. outline: true still gives the white TEXT its black outline, which does.
    return { fill: 'url(#bb-rainbow-fill-' + dieKey + ')', stroke: 'none', textFill: '#ffffff', outline: true };
  }

  var computed = getComputedStyle(swatch);
  var fill = computed.backgroundColor, textFill = computed.color;
  var isWhite = fill === 'rgb(255, 255, 255)';
  // White: no outline on the bar (Patrick, 2026-10-05: "for the white design there is no
  // border, so it should just be totally blank") - the stroke matches the fill like
  // every other colour. Was #D3D7DB, a grey outline that never prints.
  return { fill: fill, stroke: fill, textFill: textFill, outline: false, isWhite: isWhite };
}

// The reference's 0.25mm outward offset, doubled and converted to points, matching
// PRIDE_TEXT_STROKE_WIDTH_PT exactly.
var BB_OUTLINE_STROKE_WIDTH_PT = (0.25 * BB_POINTS_PER_INCH / 25.4 * 2).toFixed(4);

// SPID-282 Phase E: getComputedStyle hands colour back as "rgb(r, g, b)" - the
// order payload's fill has to be a hex string, per Simon and Odin's spec, not
// rgb(...). bbResolveStripColor's Rainbow branch already returns a literal hex
// string (there is no swatch to read a computed style from), so that case passes
// through unchanged here rather than being mis-parsed as rgb(...).
function bbColorToHex(value) {
  if (!value) return null;
  if (value.charAt(0) === '#') return value.toUpperCase();
  var match = /^rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/.exec(value);
  if (!match) return value;
  function hex2(n) {
    var h = Number(n).toString(16).toUpperCase();
    return h.length < 2 ? '0' + h : h;
  }
  return '#' + hex2(match[1]) + hex2(match[2]) + hex2(match[3]);
}

/* SPID-282 Phase E: the order payload Simon and Odin's print pipeline reads - their
 * app builds the press-ready PDF from this, the website does not produce one. Built
 * here rather than in the lazily-loaded assets/bb-advanced.js, because it has to be
 * on EVERY order, not just the ones that open the editor - see the Phase E plan
 * ("Badge Buddy designer - order data spec for Pratik (draft v1)"). Treat every key
 * below as a contract with that spec: nothing gets renamed without telling them.
 *
 * window.bbAdvDesignState() is the one seam Phase E adds, next to the Phase A/B/D
 * seams bbRenderReferenceText already calls below: it reports the box a customized
 * line was dragged or resized to, the current bar style and the added image - the
 * three things that do not already flow through this function's own placements,
 * sizing or lineFonts locals. Undefined until the editor loads, same convention as
 * the rest, so every field below tolerates that and still produces a complete
 * payload describing the standard badge.
 */
function bbDesignPlacementLine(sizing, i) {
  if (sizing.line1 && sizing.line2) return i === 0 ? 1 : 2;
  if (sizing.line1) return 1;
  if (sizing.line2) return 2;
  return null;
}

// Back of the badge (Patrick, 2026-10-05). bb-advanced.js owns the Front/Back switch and
// sets these; everything here defaults to a front-only, same-both-sides order.
window.bbCurrentSide = window.bbCurrentSide || 'front';
window.bbBackMode = window.bbBackMode || 'same';

// Hidden fields for a different back, created on first use in the product form.
// "Back Line 1/2" are visible properties (staff see the back's text on the order);
// the underscore ones are the payload for the print pipeline.
var bbBackFields = {};
function bbBackField(name) {
  if (bbBackFields[name]) return bbBackFields[name];
  var form = document.querySelector('form[data-type="add-to-cart-form"]');
  if (!form) return { value: '' };
  var el = document.createElement('input');
  el.type = 'hidden';
  el.name = name;
  form.appendChild(el);
  bbBackFields[name] = el;
  return el;
}

// Turn the back's fields on (a different back) or off (same as front) - off means
// disabled, so they never ride on an order whose back matches the front.
window.bbSetBackFieldsEnabled = function (on) {
  ['properties[_DesignBack]', 'properties[Back Line 1]', 'properties[Back Line 2]'].forEach(function (name) {
    var el = bbBackField(name);
    if (el && el.tagName) el.disabled = !on;
  });
  // The back's "make it bigger" is only ever enabled by bbEnlargeRefresh, for a different back.
  if (!on && bbBackFields['properties[Back Enlarge text]']) bbBackFields['properties[Back Enlarge text]'].disabled = true;
  if (!on && window.bbProofClearSide) window.bbProofClearSide('back');
  // A back that is the front again cannot block the order on its own.
  if (!on) bbLayoutSideIssues.back = null;
  bbLayoutRefreshGate();
};

function bbBuildDesignPayload(key, sizing, placements, lineFonts, color) {
  var designState = (typeof window.bbAdvDesignState === 'function') ? window.bbAdvDesignState() : null;
  var textFill = bbColorToHex(color && color.textFill);
  var outlineOn = !!(color && color.outline);
  var outlineWidthPt = bbDesignRoundPt(Number(BB_OUTLINE_STROKE_WIDTH_PT));

  var lines = [];
  for (var i = 0; i < placements.length; i++) {
    var p = placements[i];
    if (!p || !(p.width > 0)) continue;
    var lineNo = bbDesignPlacementLine(sizing, i);
    if (!lineNo) continue;

    var box = designState && designState.boxes && designState.boxes[lineNo];
    var moved = !!(box && box.customized);
    var fontLabel = lineNo === 1 ? (lineFonts && lineFonts.line1) : (lineFonts && lineFonts.line2);
    var lineFont = (sizing.fonts && sizing.fonts['line' + lineNo]) || fontLabel || BB_FONT_DEFAULT;

    var entry = {
      line: lineNo,
      text: p.text,
      // This line's own font (bbLayoutLineFonts) on the dies; the v3 per-line field.
      font: lineFont,
      fontSize: bbDesignRoundPt(p.fontSize),
      x: bbDesignRoundPt(p.x),
      baselineY: bbDesignRoundPt(p.baselineY),
      width: bbDesignRoundPt(p.width),
      // v4: where each character is drawn (die points), the same x list the stage SVG
      // uses - from the layout pack's advances, so production can place glyph for glyph.
      glyphX: p.glyphX ? p.glyphX.map(bbDesignRoundPt) : null,
      fill: textFill,
      moved: moved,
      box: moved ? {
        x: bbDesignRoundPt(box.x), y: bbDesignRoundPt(box.y),
        w: bbDesignRoundPt(box.w), h: bbDesignRoundPt(box.h)
      } : null
    };

    // Not part of Simon and Odin's spec - flagged to them as an addition, not
    // slipped in silently. Without it their PDF prints Rainbow text with no
    // outline while this preview shows one (bbResolveStripColor's isRainbow
    // branch, and the matching stroke bbRenderReferenceText sets on the title
    // node itself, below).
    if (outlineOn) {
      entry.outline = '#000000';
      entry.outlineWidth = outlineWidthPt;
    }
    lines.push(entry);
  }

  var img = designState && designState.image;
  var imagePayload = img ? {
    // SPID-282 Phase C4: x/y/w/h are the FRAME now, not the image's own placed
    // rect - see the BB_DESIGN_PAYLOAD_VERSION comment for why v bumped to 3.
    x: bbDesignRoundPt(img.x), y: bbDesignRoundPt(img.y),
    w: bbDesignRoundPt(img.w), h: bbDesignRoundPt(img.h),
    // The zoom percentage (of "fit inside the line") the image was placed at -
    // unchanged in meaning since Phase C2, still rides alongside x/y/w/h rather
    // than replacing them.
    fit: img.fit,
    // New in Phase C4: the image's own centre minus the frame's centre, in die
    // points - what production needs, together with `fit`, to reproduce exactly
    // what part of the image the frame shows.
    offsetX: img.offsetX, offsetY: img.offsetY,
    fileName: img.fileName, mime: img.mime,
    pxW: img.pxW, pxH: img.pxH, dpi: img.dpi
  } : null;

  // v4: the engine's own result, in its own units (inches; x from the die centre, y up
  // from the bottom cut), unrounded, so production can compare its layout of the same
  // title number for number. It describes the AUTOMATIC layout of this text; a line the
  // shopper moved is in lines[] with moved: true and its box.
  var result = sizing.layout || null;
  var layoutPayload = result ? {
    rulesVersion: result.rules_version,
    engine: window.BBLayout ? window.BBLayout.ENGINE_VERSION : null,
    packSha256: window.BB_LAYOUT_PACK_SHA256 || null,
    // "override" = the shop metafield's acronym list, "pack" = the pack's own list
    acronymsSource: bbLayoutPack() ? bbLayoutPack().acronyms_source || 'pack' : null,
    font: result.font,
    // v4 + per-line fonts: Line 2's font (= font for a one-font title) and each laid-out
    // line's font and field.
    font2: result.font2,
    mixedFontEqualSize: result.mixed_font_equal_size,
    // "Make it bigger": the two lines were sized on their own (production: properties[Enlarge text]).
    enlarge: !!result.enlarge,
    orientation: result.orientation,
    sizeClass: result.size_class,
    style: result.style,
    productionFontSizes: result.production_font_sizes,
    scale: result.scale,
    shiftIn: result.shift_in,
    needsReview: result.needs_review,
    reviewReasons: result.review_reasons,
    missingGlyphs: result.missing_glyphs,
    lines: result.lines.map(function (ln) {
      return {
        text: ln.text, field: ln.field, font: ln.font, fontSize: ln.font_size, xIn: ln.x_in,
        baselineFromCutIn: ln.baseline_from_cut_in, widthIn: ln.width_in, missingGlyphs: ln.missing_glyphs
      };
    })
  } : null;

  return {
    v: BB_DESIGN_PAYLOAD_VERSION,
    rulesVersion: result ? result.rules_version : null,
    die: key,
    units: 'pt',
    barStyle: (designState && designState.barStyle) || 'standard',
    // The back of the badge (Patrick, 2026-10-05): 'same' as this front (default) or a
    // 'different' design, which then rides separately in properties[_DesignBack]
    // (side: 'back'), with properties[_ArtworkBack] / properties[_ProofBack].
    side: window.bbCurrentSide === 'back' ? 'back' : 'front',
    back: window.bbBackMode === 'different' ? 'different' : 'same',
    lines: lines,
    image: imagePayload,
    // Characters the chosen font does not have (an order with any is blocked).
    unsupportedChars: result ? result.missing_glyphs.slice() : [],
    layout: layoutPayload
  };
}

// The VISIBLE properties[Design] flag (buy-buttons.liquid, #bb_option_design_flag).
// properties[_Design] above is underscore-prefixed and hidden from staff order
// views, ShipStation and the customer, so this is the one plain-language signal
// that an order used more than the standard badge - production and ShipStation need
// to spot that at a glance. An empty string here is what leaves the input disabled
// at the call site below, same as properties[_Design] itself used to be.
function bbDesignSummary(payload) {
  var parts = [], movedAny = false, customFont = false;
  for (var i = 0; i < payload.lines.length; i++) {
    if (payload.lines[i].moved) movedAny = true;
    if (payload.lines[i].font !== BB_FONT_DEFAULT) customFont = true;
  }
  if (movedAny) parts.push('Custom layout');
  if (customFont) parts.push('Custom font');
  if (payload.image) parts.push('Logo');
  if (payload.barStyle === 'full') parts.push('Full-width bar');
  else if (payload.barStyle === 'whole') parts.push('Whole badge');
  if (payload.back === 'different') parts.push('Different back');
  return parts.join(' + ');
}

/* SPID-282 Phase G: the proof image - a rasterized PNG of the badge exactly as drawn,
 * attached to properties[_Proof] the same DataTransfer way sections/custom-product-
 * designer.liquid attaches properties[_Artwork] (setFile there, ~lines 889-895). Simon,
 * 2026-09-22: this is what his print pipeline checks its own rebuild of the design
 * payload against, so a wrong proof is worse than no proof - it would be trusted.
 *
 * Lives here, next to bbBuildDesignPayload, for the same reason that function does:
 * a standard order (nobody ever opened the editor) still needs a proof, not just an
 * advanced one, so this cannot be lazily loaded bb-advanced.js code.
 *
 * The one thing that makes this hard: a browser rasterizing an SVG (via new Image() /
 * canvas) refuses to fetch anything that SVG references - a stylesheet's @font-face,
 * or a blob: URL. Both have to be embedded into a CLONE of the live stage SVG before
 * it is serialized and rasterized:
 *   - the web font each line is actually set to (bbProofPrepareClone resolves it the
 *     same way bbBuildDesignPayload's own `font` field does - defaulting to
 *     BB_FONT_DEFAULT, never leaving it unset the way the live page's own CSS class
 *     does for a never-customized line - see the comment on bbRenderReferenceText's
 *     own font-family write for why THAT one stays cleared. The proof is the print
 *     recipe's reference image, not a copy of what this page happens to render, so it
 *     always shows the real Minion Pro Bold rather than the Times New Roman fallback
 *     the standard preview shows for the same line);
 *   - the logo, whose <image> href is a blob: URL, read back off the File the editor
 *     already holds (window.bbAdvImageFile - bb-advanced.js) and re-encoded as a
 *     data: URL.
 *
 * bbProofFontFaceRule / bbProofBuildFontFaceBlock / bbProofPrepareMarkup are pure
 * string functions with no DOM or network calls, specifically so a Node harness can
 * exercise the embedding logic - the part that silently rasterizes wrong if it is
 * broken - without a browser. Everything that actually touches the DOM, fetch or
 * canvas lives in bbProofBuildBlob and below.
 *
 * Regenerated debounced (~350ms) after every render, same as the reference designer's
 * own scheduleProof(); the submit gate below forces an immediate render if a change
 * landed inside that window, exactly like that file's own form submit handler
 * (~lines 969-983). A proof failure must never block add to cart, so every layer here
 * - the build, the gate, the resubmit - is wrapped so the worst outcome is an order
 * with no proof attached, never a stuck submit button.
 */
var BB_PROOF_PX = 1024;

// The embedded @font-face name each font label resolves to is BB_FONT_FACES[label]'s
// own first family token (read directly off it below, never duplicated here, so the
// two can never drift) - this table only says WHICH labels have a subset file to
// fetch and embed at all. Arial has none (system font); Bodoni Bold and Garamond Bold
// have none either (assets/bb-advanced.css - the vendor files are malformed and every
// browser rejects them), so the proof renders those two at their BB_FONT_FACES
// fallback stack's system face, same as the live editor already does.
var BB_PROOF_FONT_FILES = {
  'Futura Bold': true, 'Minion Pro Bold': true, 'Rockwell': true, 'Verdana': true
};

var bbProofFontCache = {};
var bbProofImageCache = { file: null, dataUrl: null };
// Per side (Patrick, 2026-10-05: front and back can be the same or different). The
// front keeps properties[_Proof]; a different back gets its own properties[_ProofBack].
// Each side has its own debounce, revision and stale flag, so editing one side never
// supersedes the other side's proof.
var BB_PROOF_INPUT_NAMES = { front: 'properties[_Proof]', back: 'properties[_ProofBack]' };
var bbProofSides = {
  front: { args: null, timer: null, stale: false, revision: 0, input: null },
  back: { args: null, timer: null, stale: false, revision: 0, input: null }
};

// Pure: the @font-face rule for one font, or '' when there is nothing to embed (no
// base64 fetched, or no BB_FONT_FACES entry at all). family is BB_FONT_FACES[label]'s
// own first stack entry - already the quoted name the six subset files declare (see
// assets/bb-advanced.css) - so this can never embed a name the fallback chain does not
// also lead with.
function bbProofFontFaceRule(label, base64) {
  var face = BB_FONT_FACES[label];
  if (!face || !base64) return '';
  var family = face.family.split(',')[0];
  return '@font-face{font-family:' + family + ';font-weight:' + (face.bold ? '700' : '400') +
    ';font-style:normal;src:url(data:font/woff2;base64,' + base64 + ') format("woff2");}';
}

// Pure: fonts is [{label, base64}], already filtered to the labels actually in use on
// the two lines (bbProofPrepareClone) - never all four with a file, so the harness's
// "not for the fonts that are not" assertion holds by construction, not by luck.
function bbProofBuildFontFaceBlock(fonts) {
  var css = '';
  for (var i = 0; i < fonts.length; i++) css += bbProofFontFaceRule(fonts[i].label, fonts[i].base64);
  return css ? '<style>' + css + '</style>' : '';
}

// Pure: the string-level embedding step - inserts the font-face block as the SVG's
// first child and replaces the image's blob: href (both attribute spellings, plain
// and xlink:) with a self-contained data: URL. String-based rather than a second DOM
// pass so a Node harness can feed it literal markup with no DOM at all.
function bbProofPrepareMarkup(svgMarkup, fontFaceBlock, imageDataUrl) {
  var markup = svgMarkup;
  if (imageDataUrl) {
    markup = markup.replace(/(xlink:href|href)="blob:[^"]*"/g, function (match, attr) {
      return attr + '="' + imageDataUrl + '"';
    });
  }
  if (fontFaceBlock) markup = markup.replace(/(<svg\b[^>]*>)/, '$1' + fontFaceBlock);
  return markup;
}

function bbProofResolvedFont(lineFonts, lineNo) {
  var label = lineNo === 1 ? (lineFonts && lineFonts.line1) : (lineNo === 2 ? (lineFonts && lineFonts.line2) : null);
  return label || BB_FONT_DEFAULT;
}

// PROOF wide (or tall), the other edge proportional to the die - the same intent as
// the reference designer's own renderProofBlob (PROOF x proportional height), but
// keyed off whichever die edge is actually longer so a vertical badge does not come
// out as a 1024 x 2100 image.
function bbProofDieCanvasSize(geometry) {
  var longEdge = Math.max(geometry.dieWidth, geometry.dieHeight);
  var scale = BB_PROOF_PX / longEdge;
  return { w: Math.round(geometry.dieWidth * scale), h: Math.round(geometry.dieHeight * scale) };
}

// Clones the live stage SVG and makes it self-sufficient for rasterization: sizes it
// to the proof's own pixel dimensions, and writes each title node's REAL font
// (defaulting to BB_FONT_DEFAULT, unlike the live page - see the doc comment above)
// so what gets embedded matches what gets rasterized. Returns the labels that still
// need a subset file fetched (bbProofBuildBlob) - never Arial/Bodoni/Garamond, which
// have none.
// Paint that comes from the stylesheet has to be written onto the clone as inline
// style before it is rasterized. A serialized SVG carries no stylesheet, so anything
// whose colour lives in custom-bb.css - the slot and both watermark lines - falls back
// to the SVG default of BLACK in the proof while looking correct on screen. That was
// wrong from the day the proof was built and was only caught by sampling the finished
// PNG's pixels (the slot came back 0,0,0 against the 213,217,220 the page shows).
// Walking both trees together and copying the COMPUTED value covers whatever gets added
// to the badge later, rather than listing today's three nodes and drifting again.
// textAnchor (2026-10-05): the watermark is centred by .bb-stage__watermark's
// text-anchor: middle, which the proof lost, so it printed starting at the centre line.
// Type as well as paint (2026-10-07): the watermark's family, weight and letter-spacing
// also come from custom-bb.css, so the proof drew it in a serif at the wrong weight
// (Odin's QA, 2026-10-06). The title lines set their own family/weight afterwards in
// bbProofPrepareClone, so copying the computed values here never overrides them.
var BB_PROOF_PAINT_PROPS = ['fill', 'stroke', 'strokeWidth', 'fillOpacity', 'strokeOpacity', 'opacity', 'textAnchor',
  'fontFamily', 'fontWeight', 'fontStyle', 'letterSpacing'];

function bbProofInlineComputedPaint(liveRoot, cloneRoot) {
  var live = liveRoot.querySelectorAll('*');
  var copy = cloneRoot.querySelectorAll('*');
  if (live.length !== copy.length) return; // trees out of step - leave the clone alone
  for (var i = 0; i < live.length; i++) {
    var cs;
    try { cs = getComputedStyle(live[i]); } catch (e) { continue; }
    if (!cs) continue;
    for (var p = 0; p < BB_PROOF_PAINT_PROPS.length; p++) {
      var prop = BB_PROOF_PAINT_PROPS[p];
      var value = cs[prop];
      if (value) copy[i].style[prop] = value;
    }
  }
}

function bbProofPrepareClone(svg, geometry, sizing, placements, lineFonts) {
  var clone = svg.cloneNode(true);
  var size = bbProofDieCanvasSize(geometry);
  clone.setAttribute('width', size.w);
  clone.setAttribute('height', size.h);
  bbProofInlineComputedPaint(svg, clone);

  // Badge Bottoms (2026-10-18): the stage draws the die inside a photo of the holder
  // (bb-stage-svg.liquid, .bb-stage__pocket). The proof is the print area only, so the
  // holder is dropped and the pocket's die coordinates become the whole image.
  var pocket = clone.querySelector('.bb-stage__pocket');
  if (pocket) {
    var holder = clone.querySelector('.bb-stage__holder');
    if (holder && holder.parentNode) holder.parentNode.removeChild(holder);
    var vb = (pocket.getAttribute('viewBox') || '').split(/[\s,]+/);
    clone.setAttribute('viewBox', pocket.getAttribute('viewBox'));
    pocket.setAttribute('x', '0');
    pocket.setAttribute('y', '0');
    pocket.setAttribute('width', vb[2]);
    pocket.setAttribute('height', vb[3]);
  }

  var titleNodes = clone.querySelectorAll('[data-bb-title-line]');
  var usedFonts = {}, usedList = [];
  for (var i = 0; i < titleNodes.length; i++) {
    var node = titleNodes[i], p = placements[i];
    if (!p || !(p.width > 0)) continue;
    var lineNo = bbDesignPlacementLine(sizing, i);
    var label = bbProofResolvedFont(lineFonts, lineNo);
    var face = BB_FONT_FACES[label];
    if (face) {
      node.style.fontFamily = face.family;
      node.style.fontWeight = face.bold ? 'bold' : 'normal';
    }
    if (BB_PROOF_FONT_FILES[label] && !usedFonts[label]) { usedFonts[label] = true; usedList.push(label); }
  }

  return { clone: clone, size: size, usedFonts: usedList };
}

function bbProofFontUrl(label) {
  var urls = window.BB_ADVANCED_ASSET_URLS;
  if (label === BB_PROOF_WATERMARK_FONT) return urls ? (urls.watermarkFont || null) : null;
  return urls && urls.fonts ? urls.fonts[label] : null;
}

// The watermark's own font, embedded alongside the title fonts. Its key in the font
// cache and loader; never a BB_FONT_FACES label, so it cannot collide with one.
var BB_PROOF_WATERMARK_FONT = '__watermark';

// Pure: the @font-face rule for the watermark - the family is the first name in its
// computed stack (the one the clone asks for), weight and style as computed. '' when
// there is no file or no family to name.
function bbProofWatermarkFontFaceRule(family, weight, style, base64) {
  var name = String(family || '').split(',')[0].trim();
  if (!name || !base64) return '';
  return '@font-face{font-family:' + name + ';font-weight:' + (weight || '400') +
    ';font-style:' + (style || 'normal') + ';src:url(data:font/woff2;base64,' + base64 + ') format("woff2");}';
}

// Chunked to stay under String.fromCharCode's argument-count ceiling on a ~50KB font.
function bbArrayBufferToBase64(buffer) {
  var bytes = new Uint8Array(buffer), binary = '', chunk = 0x8000;
  for (var i = 0; i < bytes.length; i += chunk) {
    binary += String.fromCharCode.apply(null, bytes.subarray(i, i + chunk));
  }
  return btoa(binary);
}

// Cached across proof regenerations - the same two or three fonts get re-fetched
// on every debounced render otherwise, for bytes that never change mid-session.
function bbProofLoadFontBase64(label, cb) {
  if (bbProofFontCache[label]) { cb(bbProofFontCache[label]); return; }
  var url = bbProofFontUrl(label);
  if (!url || typeof fetch !== 'function') { cb(null); return; }

  // The reference designer's proof path (sections/custom-product-designer.liquid) needs
  // no timeout anywhere because it only ever touches a canvas, which cannot hang. Ours
  // fetches a font file, and a request that never settles would leave the submit gate
  // below waiting forever. So the bound lives HERE, on the one unbounded wait, rather
  // than as a blanket cap on the submit - which keeps that gate the same shape as the
  // reference's. Giving up on the font costs a proof in the fallback typeface, never
  // the order.
  var settled = false;
  function done(value) {
    if (settled) return;
    settled = true;
    cb(value);
  }
  setTimeout(function () { done(null); }, 1500);

  fetch(url).then(function (res) {
    if (!res.ok) throw new Error('bb-proof-font-fetch-failed');
    return res.arrayBuffer();
  }).then(function (buf) {
    var b64 = bbArrayBufferToBase64(buf);
    bbProofFontCache[label] = b64;
    done(b64);
  }).catch(function () { done(null); });
}

function bbProofLoadFonts(labels, cb) {
  var results = {}, remaining = labels.length;
  if (remaining === 0) { cb(results); return; }
  function doneOne() { remaining--; if (remaining === 0) cb(results); }
  for (var i = 0; i < labels.length; i++) {
    (function (label) {
      bbProofLoadFontBase64(label, function (b64) { if (b64) results[label] = b64; doneOne(); });
    })(labels[i]);
  }
}

// Reads the File the editor already holds (window.bbAdvImageFile - bb-advanced.js,
// undefined until that script loads) back as a data: URL. Cached by File identity so
// dragging or resizing the image does not re-encode it on every debounced render.
// The logo File is captured with the proof's args when it is scheduled (bbScheduleProof),
// not read at render time - by then the shopper may be looking at the other side.
function bbProofImageDataUrl(file, cb) {
  if (!file) { cb(null); return; }
  if (bbProofImageCache.file === file && bbProofImageCache.dataUrl) { cb(bbProofImageCache.dataUrl); return; }
  try {
    var reader = new FileReader();
    reader.onload = function () { bbProofImageCache = { file: file, dataUrl: reader.result }; cb(reader.result); };
    reader.onerror = function () { cb(null); };
    reader.readAsDataURL(file);
  } catch (e) { cb(null); }
}

// The only function here that touches fetch/FileReader/XMLSerializer/canvas. Always
// calls back - with a Blob on success, with nothing on any failure (a tainted canvas,
// a font fetch that failed, toBlob returning null) - never throws past its own
// try/catch, per the Phase G rule that a proof failure must never block add to cart.
function bbProofBuildBlob(args, cb) {
  var prepared;
  try {
    prepared = bbProofPrepareClone(args.svg, args.geometry, args.sizing, args.placements, args.lineFonts);
  } catch (e) { cb(null); return; }

  // The watermark's computed type, read off the live node - the same values
  // bbProofInlineComputedPaint just wrote onto the clone.
  var wmNode = args.svg.querySelector('[data-bb-watermark-one]');
  var wm = null;
  try { if (wmNode) { var wcs = getComputedStyle(wmNode); wm = { family: wcs.fontFamily, weight: wcs.fontWeight, style: wcs.fontStyle }; } } catch (e) { wm = null; }
  var toLoad = prepared.usedFonts.slice();
  if (wm && bbProofFontUrl(BB_PROOF_WATERMARK_FONT)) toLoad.push(BB_PROOF_WATERMARK_FONT);

  bbProofLoadFonts(toLoad, function (fontResults) {
    var fontList = [];
    for (var i = 0; i < prepared.usedFonts.length; i++) {
      var label = prepared.usedFonts[i];
      if (fontResults[label]) fontList.push({ label: label, base64: fontResults[label] });
    }
    var fontFaceBlock = bbProofBuildFontFaceBlock(fontList);
    var wmRule = wm ? bbProofWatermarkFontFaceRule(wm.family, wm.weight, wm.style, fontResults[BB_PROOF_WATERMARK_FONT]) : '';
    if (wmRule) fontFaceBlock = fontFaceBlock ? fontFaceBlock.replace('</style>', wmRule + '</style>') : '<style>' + wmRule + '</style>';

    bbProofImageDataUrl(args.imageFile || null, function (imageDataUrl) {
      try {
        var markup = new XMLSerializer().serializeToString(prepared.clone);
        markup = bbProofPrepareMarkup(markup, fontFaceBlock, imageDataUrl);
        var dataUrl = 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(markup);

        var img = new Image();
        img.onload = function () {
          try {
            var canvas = document.createElement('canvas');
            canvas.width = prepared.size.w;
            canvas.height = prepared.size.h;
            canvas.getContext('2d').drawImage(img, 0, 0, prepared.size.w, prepared.size.h);
            canvas.toBlob(function (blob) { cb(blob || null); }, 'image/png');
          } catch (e) { cb(null); }
        };
        img.onerror = function () { cb(null); };
        img.src = dataUrl;
      } catch (e) { cb(null); }
    });
  });
}

// properties[_Proof]: created lazily in the theme's own product form, the same
// DataTransfer technique as properties[_Artwork] (bb-advanced.js bbAdvSetArtworkFile,
// itself copied from sections/custom-product-designer.liquid's hiddenInput()/setFile()).
// Lives here rather than as a static input in buy-buttons.liquid so a product with no
// die profile (Badge Bottoms) - which never calls bbScheduleProof at all - never grows
// an empty file field it has no way to fill.
function bbProofFileInput(side) {
  var st = bbProofSides[side || 'front'];
  if (st.input) return st.input;
  var form = document.querySelector('form[data-type="add-to-cart-form"]');
  if (!form) return null;
  var el = document.createElement('input');
  el.type = 'file';
  el.name = BB_PROOF_INPUT_NAMES[side || 'front'];
  el.style.display = 'none';
  el.tabIndex = -1;
  form.appendChild(el);
  st.input = el;
  return el;
}

function bbProofSetFile(blob, side) {
  var input = bbProofFileInput(side);
  if (!input || !blob) return;
  try {
    var dt = new DataTransfer();
    dt.items.add(new File([blob], side === 'back' ? 'print-proof-back.png' : 'print-proof.png', { type: 'image/png' }));
    input.files = dt.files;
    input.disabled = false;
  } catch (e) { /* very old browsers: order proceeds without the proof */ }
}

// The back's proof is dropped (input emptied) whenever the back is not a different
// design, so an order never carries a stale back proof. bb-advanced.js calls this.
window.bbProofClearSide = function (side) {
  var st = bbProofSides[side];
  if (!st) return;
  clearTimeout(st.timer);
  st.stale = false;
  st.args = null;
  st.revision++;
  // Disabled as well as emptied, so an empty file field never rides on the order.
  if (st.input) { try { st.input.value = ''; } catch (e) { /* ignore */ } st.input.disabled = true; }
};

// revision guards a render that was superseded before it finished - the same pattern
// as the reference designer's own imageRevision/proofRevision pair: only the render
// that is still current when it completes gets to touch the file input or clear the
// stale flag, so a slow font fetch from an earlier keystroke can never clobber a
// faster one from a later keystroke.
function bbProofRenderNow(side, revision, cb) {
  var st = bbProofSides[side];
  var args = st.args;
  if (!args || !args.svg) { cb(); return; }
  try {
    // bbProofBuildBlob clones the live SVG synchronously before anything async, so a
    // flush right before a side switch (window.bbProofFlush) captures this side.
    bbProofBuildBlob(args, function (blob) {
      if (revision === st.revision) {
        if (blob) bbProofSetFile(blob, side);
        st.stale = false;
      }
      cb();
    });
  } catch (e) { cb(); }
}

// Render a side's pending proof now. bb-advanced.js calls this right before it swaps
// the stage to the other side, while the stage still shows this one.
window.bbProofFlush = function (side) {
  var st = bbProofSides[side];
  if (!st || !st.stale) return;
  clearTimeout(st.timer);
  bbProofRenderNow(side, st.revision, function () {});
};

// Called from bbRenderReferenceText on every render, standard or advanced - see the
// doc comment above this section for why a standard order needs a proof too. Debounced
// 350ms, same as the reference designer's own scheduleProof(), so a shopper typing
// does not fetch/rasterize on every keystroke.
function bbScheduleProof(svg, key, geometry, sizing, placements, lineFonts, side) {
  side = side || 'front';
  var st = bbProofSides[side];
  var getFile = window.bbAdvImageFile;
  st.args = {
    svg: svg, key: key, geometry: geometry, sizing: sizing, placements: placements, lineFonts: lineFonts,
    imageFile: typeof getFile === 'function' ? getFile() : null
  };
  st.revision++;
  st.stale = true;
  clearTimeout(st.timer);
  var revision = st.revision;
  st.timer = setTimeout(function () { bbProofRenderNow(side, revision, function () {}); }, 350);
}

// Submit gate: if a change landed inside the debounce window, hold the submit just
// long enough to render and attach a fresh proof, then resubmit - exactly the pattern
// custom-bb-required.js already uses for the Line 1 gate (capture on document, so this
// runs before product-form.js's own AJAX submit handler), and the same shape as the
// reference designer's own form submit handler (sections/custom-product-designer.liquid,
// ~lines 969-983), which renders and then resubmits with no timer of its own. What makes
// "a proof failure must never block add to cart" true here is that every path into
// bbProofRenderNow calls its callback exactly once - the font fetch is bounded at its
// own call site, canvas errors are caught, and the outer try/catch covers the rest.
document.addEventListener('submit', function (e) {
  try {
    var form = e.target;
    if (!form || typeof form.querySelector !== 'function') return;
    // Only the front can still be stale here: bb-advanced.js switches a shopper on the
    // back to the front before this runs (window capture), flushing the back's proof.
    var st = bbProofSides.front;
    if (!st.stale) return;
    var designInput = document.getElementById('bb_option_design');
    if (!designInput || !form.contains(designInput)) return;

    e.preventDefault();
    e.stopImmediatePropagation();

    var revision = st.revision, settled = false;
    function finish() {
      if (settled) return;
      settled = true;
      if (revision === st.revision) st.stale = false;
      try {
        if (typeof form.requestSubmit === 'function') form.requestSubmit(); else form.submit();
      } catch (err) { /* nothing more that can be done for this submit */ }
    }

    clearTimeout(st.timer);
    try { bbProofRenderNow('front', revision, finish); } catch (err) { finish(); }
  } catch (err) {
    /* A proof failure must never block add to cart - let the native submit through. */
  }
}, true);

/* Draws the die-profile badge as one SVG in die coordinates: body, slot, strip,
   watermark and title, all in the stage markup rendered by bb-stage-svg.liquid. Runs
   on every text or colour change and simply recomputes everything, same as the
   production renderer's own render() - the geometry never actually changes at
   runtime, but recomputing it is cheap and keeps this one function rather than two
   that have to stay in step. */
function bbRenderReferenceText() {
  var key = bbDieKey();
  var geometry = bbCalculateGeometry(key);
  var tab2 = document.getElementById('tab2');
  var svg = tab2 && tab2.querySelector('[data-bb-svg]');
  if (!geometry || !svg) return false;

  function setNum(el, name, value) { if (el) el.setAttribute(name, value); }

  var body = svg.querySelector('[data-bb-body]');
  var highlight = svg.querySelector('[data-bb-body-highlight]');
  var isClear = tab2.getAttribute('data-bb-clear') === 'true';
  if (body) {
    body.setAttribute('fill', isClear ? '#eef3f6' : '#ffffff');
    body.setAttribute('fill-opacity', isClear ? '0.6' : '1');
    body.setAttribute('stroke', isClear ? '#b9c3cb' : '#cdd3d8');
    body.setAttribute('stroke-width', '1.2');
  }
  if (highlight) highlight.style.display = isClear ? '' : 'none';

  var swatch = tab2.querySelector('.selectedColor');
  var color = swatch ? bbResolveStripColor(swatch, key) : null;
  var strip = svg.querySelector('[data-bb-strip]');
  if (strip && color) {
    strip.setAttribute('fill', color.fill);
    strip.setAttribute('stroke', color.stroke);
  }

  // SPID-282 Phase B: the one seam the lazily-loaded advanced editor gets to repaint
  // the bar as a full-width band or the whole badge in colour, next to the Phase A
  // seam below. Undefined until a shopper has opened the editor, so a page that never
  // does renders the strip exactly as set above - this line is a no-op for them.
  // Reassigning geometry (rather than just repainting the SVG) is what lets a wider
  // band also widen the editor's drag/resize clamp and the automatic text layout
  // computed just below, both of which read stripX/stripWidth off this same object.
  if (typeof window.bbAdvApplyBarStyle === 'function') {
    geometry = window.bbAdvApplyBarStyle(svg, geometry, color, key) || geometry;
  }

  // SPID-282 Phase D: the seam the lazily-loaded advanced editor gets to report
  // which font each line is set to, next to the Phase A/B seams below. Undefined
  // until a shopper has opened the editor AND picked a non-default font, so a page
  // that never does lays the title out in BB_FONT_DEFAULT.
  var lineFonts = (typeof window.bbAdvLineFonts === 'function') ? window.bbAdvLineFonts() : null;

  // BB placement rules v1: production's layout engine places the title (see
  // bbAutoLayout). The bar style picks its style - Standard bar 'box', Full-width bar
  // and Whole badge 'bleed' - and each line is set in its own font.
  var barStyle = (typeof window.bbAdvBarStyle === 'function') ? window.bbAdvBarStyle() : 'standard';
  var drawFonts = bbLayoutLineFonts(lineFonts);
  var side = (window.bbCurrentSide === 'back' && window.bbBackMode === 'different') ? 'back' : 'front';
  var titleNodes = svg.querySelectorAll('[data-bb-title-line]');

  var sample = bbSampleTextFor(side);
  var auto = bbAutoLayout(key, geometry, sample || badgeText, sample ? '' : badgeText2, drawFonts, barStyle, side);
  bbEnlargeRefresh(side, auto && !sample && auto.enlarge);
  // Typing anything starts the blank question over (clear it again and the sample returns).
  if (window.bbBlankAsked && !bbFrontIsEmpty()) window.bbBlankAsked = false;
  // The sample is only a preview, so it is never checked for fit; the typed text still
  // goes through BB_PLACEHOLDER_RE either way, and a blank front waits to be asked.
  var issues = sample && auto
    ? { unavailable: false, belowMin: false, belowLines: [], missing: [], missingLines: [], font: null, twoLines: false }
    : bbLayoutIssuesFor(auto);
  bbLayoutWithPlaceholder(issues, badgeText, badgeText2);
  issues.askBlank = bbBlankNeedsAsk(side);
  bbLayoutSideIssues[side] = issues;
  if (!auto) {
    // The engine or the pack did not load: nothing here can say where the text would
    // print, so draw no title and hold the order (bbLayoutRefreshGate) rather than
    // guess.
    for (var h = 0; h < titleNodes.length; h++) {
      titleNodes[h].textContent = '';
      titleNodes[h].setAttribute('visibility', 'hidden');
    }
    bbLastSizing = { line1: bbLayoutStrip(badgeText), line2: bbLayoutStrip(badgeText2), fontSize1: 0, fontSize2: 0 };
    bbLayoutRefreshGate();
    return true;
  }
  var sizing = auto.sizing;
  var placements = auto.placements;

  // SPID-282 Phase A: the one seam the lazily-loaded advanced text editor
  // (assets/bb-advanced.js) gets into this function. It only replaces the placement
  // of a line the shopper moved or resized; every other line is drawn exactly where
  // the engine put it.
  if (typeof window.bbApplyCustomLayout === 'function') {
    placements = window.bbApplyCustomLayout(placements, sizing, geometry) || placements;
  }

  for (var i = 0; i < titleNodes.length; i++) {
    var node = titleNodes[i], p = placements[i];
    if (!p || !(p.width > 0)) {
      if (node) {
        node.textContent = '';
        node.removeAttribute('textLength');
        node.removeAttribute('lengthAdjust');
        node.setAttribute('visibility', 'hidden');
      }
      continue;
    }

    // This line's own font: the engine's answer, or (a line the shopper moved, whose
    // placement the editor rebuilt) its field's picker.
    var lineFont = p.font || drawFonts['line' + (bbDesignPlacementLine(sizing, i) || 1)];
    // One x per character (the engine's glyph offsets; a moved line gets the same
    // offsets for its own size), so every letter sits where production's PDF puts it.
    // No textLength: nothing stretches or squeezes the glyphs any more.
    var xs = p.glyphX || bbLayoutGlyphX(lineFont, p.text, p.fontSize, p.x);
    if (xs && !p.glyphX) p.glyphX = xs;
    // A letter the font does not have is drawn as "?" in the badge font, inside the
    // space the engine reserved for it: the browser's fallback glyph (an emoji, a
    // heart) can be far wider and ran off the bar (Pratik, 2026-10-11). The order is
    // blocked until the letter is removed, so this only affects the preview.
    var missing = p.missing || [];
    var drawText = p.text, drawXs = xs;
    if (missing.length) {
      // One "?" per character the customer can see (2026-10-13). An emoji is often
      // several code points - a heart plus a variation selector, a woman + zero-width
      // joiner + medical symbol for one nurse emoji - and each used to draw its own "?".
      // The text is walked in user-perceived characters (Intl.Segmenter graphemes): a
      // character with any missing part becomes a single "?" at its first code point's
      // x, and the positions of its other parts are dropped, so every letter after it
      // keeps the x the engine gave it (engine positions are per code point, the unit
      // Array.from walks). Without Segmenter, invisible joiners and selectors are still
      // dropped, which covers the common single-symbol emoji.
      var keptChars = [], keptXs = [];
      var cps = Array.from(p.text);
      var groups = null;
      try {
        if (typeof Intl !== 'undefined' && Intl.Segmenter) {
          groups = Array.from(new Intl.Segmenter(undefined, { granularity: 'grapheme' }).segment(p.text), function (s) { return Array.from(s.segment); });
        }
      } catch (e) { groups = null; }
      if (!groups) groups = cps.map(function (ch) { return [ch]; });
      var invisible = function (ch) { return /^[︀-️​-‍⁠]$/.test(ch); };
      var k = 0;
      groups.forEach(function (g) {
        var hasMissing = g.some(function (ch) { return missing.indexOf(ch) >= 0; });
        if (g.every(invisible)) {
          // nothing the customer can see - draws nothing
        } else if (hasMissing) {
          keptChars.push('?');
          if (xs) keptXs.push(xs[k]);
        } else {
          g.forEach(function (ch, j) {
            if (invisible(ch)) return;
            keptChars.push(ch);
            if (xs) keptXs.push(xs[k + j]);
          });
        }
        k += g.length;
      });
      drawText = keptChars.join('');
      if (xs) drawXs = keptXs;
    }
    node.textContent = drawText;
    node.setAttribute('visibility', 'visible');
    if (sample) node.setAttribute('opacity', '0.35');
    else node.removeAttribute('opacity');
    node.setAttribute('x', drawXs ? drawXs.map(function (v) { return v.toFixed(4); }).join(' ') : p.x.toFixed(4));
    setNum(node, 'y', p.baselineY.toFixed(4));
    setNum(node, 'font-size', p.fontSize);
    node.removeAttribute('textLength');
    node.removeAttribute('lengthAdjust');
    // Inline as well as in custom-bb.css, so the proof (a serialized clone with no
    // stylesheet) is drawn without kerning or ligatures too.
    node.style.fontKerning = 'none';
    node.style.fontVariantLigatures = 'none';
    node.style.fontFeatureSettings = '"kern" 0, "liga" 0';

    // SPID-282 Phase D: an inline style, not a presentation attribute - .bb-stage__title
    // (custom-bb.css) already sets font-family/font-weight, and a stylesheet rule always
    // beats a presentation attribute for the same property regardless of write order,
    // the same reason bbAdvApplyBarStyle's watermark repaint (assets/bb-advanced.js)
    // uses .style.fill rather than setAttribute. Cleared (both set to '') for the
    // default font, which falls through to that CSS rule.
    var face = (lineFont !== BB_FONT_DEFAULT) ? BB_FONT_FACES[lineFont] : null;
    node.style.fontFamily = face ? face.family : '';
    node.style.fontWeight = face ? (face.bold ? 'bold' : 'normal') : '';

    if (color) {
      node.setAttribute('fill', color.textFill);
      if (color.outline) {
        node.setAttribute('stroke', '#000000');
        setNum(node, 'stroke-width', BB_OUTLINE_STROKE_WIDTH_PT);
        node.setAttribute('stroke-linejoin', 'round');
        node.setAttribute('paint-order', 'stroke fill');
      } else {
        node.removeAttribute('stroke');
        node.removeAttribute('stroke-width');
        node.removeAttribute('stroke-linejoin');
        node.removeAttribute('paint-order');
      }
    }
  }
  lineFonts = drawFonts;

  if (sample) {
    bbLastSizing = { line1: '', line2: '', fontSize1: 0, fontSize2: 0 };
    bbLayoutRefreshGate();
    return true;
  }

  // SPID-282 Phase E: properties[_Design] now carries a complete order payload on
  // EVERY render, not only once the editor has customized something - see the doc
  // comment above bbBuildDesignPayload for why this moved here from
  // assets/bb-advanced.js. Guarded on the input existing at all: buy-buttons.liquid
  // only renders it where bb_custom_lines is true (see that file), and this
  // function can in principle run before that markup exists.
  var designInput = document.getElementById('bb_option_design');
  if (designInput && window.bbCurrentSide === 'back') {
    // Editing a different back: its payload and proof go to the back's own fields;
    // the front's properties[_Design] keeps what the front last wrote.
    var backInput = bbBackField('properties[_DesignBack]');
    if (backInput) {
      backInput.disabled = false;
      backInput.value = JSON.stringify(bbBuildDesignPayload(key, sizing, placements, lineFonts, color));
    }
    try { bbScheduleProof(svg, key, geometry, sizing, placements, lineFonts, 'back'); } catch (e) { /* proof only */ }
  } else if (designInput) {
    var designPayload = bbBuildDesignPayload(key, sizing, placements, lineFonts, color);
    designInput.disabled = false;
    designInput.value = JSON.stringify(designPayload);

    // properties[Design] - the visible counterpart, see bbDesignSummary's own
    // comment for why it exists and what disables it.
    var designFlagInput = document.getElementById('bb_option_design_flag');
    if (designFlagInput) {
      var designSummary = bbDesignSummary(designPayload);
      designFlagInput.disabled = designSummary === '';
      designFlagInput.value = designSummary;
    }

    // SPID-282 Phase G: schedule the proof PNG next to the payload above, for the
    // same reason it lives here at all - a standard order needs one too. Guarded
    // against throwing so a broken proof path can never break the render pass that
    // writes properties[_Design] itself.
    try { bbScheduleProof(svg, key, geometry, sizing, placements, lineFonts); } catch (e) { /* proof only - never the render */ }
  }

  bbLastSizing = sizing;
  bbLayoutRefreshGate();
  return true;
}

/* Places the colour plate and the two watermark lines by die fractions, against the
   measured artwork box. `art` is {x, y, w, h} in rendered pixels. */
function applyDieGeometry(artFrac) {
  var die = bbDieProfile();
  if (!die || !artFrac || !artFrac.wFrac || !artFrac.hFrac) return;

  var tab2 = document.getElementById('tab2');
  var plate = tab2 && tab2.querySelector('.selectedColor');
  var image = tab2 && tab2.querySelector('img.selectedBadge');
  if (!plate || !image) return;

  // Resolve against the image's live rect. The width #tab2 is *given* and the width it
  // actually renders at can differ - the pane caps it - and scaling die fractions by the
  // asked-for width left the plate about 1.5px narrow, which reads as the plate sitting
  // off-centre on the badge.
  var ib = image.getBoundingClientRect(), tb = tab2.getBoundingClientRect();
  var art = {
    x: (ib.left - tb.left) + artFrac.xFrac * ib.width,
    y: (ib.top - tb.top) + artFrac.yFrac * ib.height,
    w: artFrac.wFrac * ib.width,
    h: artFrac.hFrac * ib.height,
    slotBottomFrac: artFrac.slotBottomFrac
  };

  var stripX = (die.dieW - die.stripW) / 2;
  var stripY = die.dieH - die.stripBottomInset - die.stripH;

  // Absolute inside #tab2 so the plate no longer depends on the negative-margin flow
  // trick, which could only ever be expressed against the image canvas.
  tab2.style.position = 'relative';
  plate.style.position = 'absolute';
  plate.style.margin = '0';
  plate.style.aspectRatio = 'auto';
  plate.style.left = (art.x + (stripX / die.dieW) * art.w).toFixed(2) + 'px';
  plate.style.top = (art.y + (stripY / die.dieH) * art.h).toFixed(2) + 'px';
  plate.style.width = ((die.stripW / die.dieW) * art.w).toFixed(2) + 'px';
  plate.style.height = ((die.stripH / die.dieH) * art.h).toFixed(2) + 'px';

  var one = tab2.querySelector('[data-bb-watermark-one]');
  var two = tab2.querySelector('[data-bb-watermark-two]');
  if (!one || !two) return;

  var size1 = 5.2 / BB_POINTS_PER_INCH;
  var size2 = 6.5 / BB_POINTS_PER_INCH;
  var gap = 0.12;

  // The die puts the slot at 7.3%-11.3% of the badge height. Our artwork draws it bigger
  // and higher - measured at 2.9%-14.9% on the standard horizontal - so a watermark placed
  // purely from the die lands on top of it and hides it, which is exactly what happened.
  // Keep the reference's gap below the slot, but measure the slot we actually have.
  var dieBase1 = (die.slotTop + die.slotH + gap) / die.dieH;
  var artBase1 = art.slotBottomFrac != null ? art.slotBottomFrac + gap / die.dieH : 0;
  var base1 = Math.max(dieBase1, artBase1);
  var base2 = base1 + (size1 + 0.02) / die.dieH;

  // The renderer positions SVG text by baseline; these are HTML boxes, so the baseline is
  // approximated at 0.8em from the top - Poppins' ascent. Close enough at this scale: the
  // card is within 0.1% of the die's proportion.
  function place(node, baselineFrac, sizeIn) {
    var px = (sizeIn / die.dieH) * art.h;
    node.style.position = 'absolute';
    node.style.left = (art.x + art.w / 2).toFixed(2) + 'px';
    node.style.top = (art.y + baselineFrac * art.h - px * 0.8).toFixed(2) + 'px';
    node.style.fontSize = px.toFixed(2) + 'px';
    node.style.transform = 'translateX(-50%)';
  }
  place(one, base1, size1);
  place(two, base2, size2);

  // textfill sizes the badge text to fit the plate as the shopper types, but this runs
  // afterwards - on image load - and changes the plate's size. Without re-fitting, the
  // text keeps the size it was given for the old plate and spills over the new one,
  // which is what clipped the first line at the bottom.
  if (typeof window.bbSyncFromFields === 'function') window.bbSyncFromFields();
}

/* Keeps the visible badge centred in its pane.
 *
 * The badge images carry uneven transparent padding - bbhorizontal.webp has 1px of empty
 * pixels on the left against 20px on the right, and 29px on top against 0 at the bottom
 * once the crop has run. Centring the element therefore leaves the badge itself visibly
 * off-centre, and sizing it by the canvas leaves it narrower than its own box.
 *
 * Rather than hard-code offsets per variant, this reads each image's alpha bounds and
 * both sizes and trims from them, so it self-corrects across all eight variants and any
 * artwork swapped in later.
 *
 * A workaround for the padding being in the files at all. Artwork re-exported trimmed to
 * the badge would make it unnecessary - see SPID-185.
 */
/* Centres the visible badge, not #tab2's box.
 *
 * The badge images carry uneven transparent padding - bbhorizontal.webp has 1px of
 * empty pixels on the left but 20px on the right, and 29px on top against 0 at the
 * bottom once the crop has run. Centring the element therefore leaves the badge
 * itself visibly off-centre: measured 13px left against 32px right, and 80px top
 * against 51px bottom.
 *
 * Rather than hard-code per-variant offsets, this reads each image's alpha bounds and
 * compensates. It self-corrects for all eight variants and for any artwork swapped in
 * later. Margins are used instead of a transform so it cannot collide with the size
 * demo, which owns #tab2's transform. In a centred flex item a margin on one side
 * shifts the item by half that margin, which is why the full asymmetry is applied.
 *
 * This is a workaround for the padding being in the files at all. Re-exported artwork
 * trimmed to the badge would make it unnecessary. */
function centreArtwork() {
  var tab2 = document.getElementById('tab2');
  if (!tab2) return;
  var img = tab2.querySelector('img.selectedBadge');
  var src = img && (img.currentSrc || img.src);
  if (!src) return;

  var probe = new Image();
  probe.crossOrigin = 'anonymous';
  probe.onload = function () {
    var w = probe.naturalWidth, h = probe.naturalHeight;
    if (!w || !h) return;
    var c = document.createElement('canvas');
    c.width = w; c.height = h;
    var ctx = c.getContext('2d', { willReadFrequently: true });
    ctx.drawImage(probe, 0, 0);
    var d;
    try { d = ctx.getImageData(0, 0, w, h).data; } catch (e) { return; }

    var minX = w, maxX = -1, minY = h, maxY = -1, transparent = false;
    for (var y = 0; y < h; y++) {
      for (var x = 0; x < w; x++) {
        var i = (y * w + x) * 4;
        if (d[i + 3] < 12) { transparent = true; continue; }
        // Files without an alpha channel sit on white, so treat white as empty there.
        if (!transparent && d[i] > 246 && d[i + 1] > 246 && d[i + 2] > 246) continue;
        if (x < minX) minX = x;
        if (x > maxX) maxX = x;
        if (y < minY) minY = y;
        if (y > maxY) maxY = y;
      }
    }
    if (maxX < 0) return;

    // Only the photo path (Badge Bottoms) reaches here now - the Badge Buddy dies are drawn
    // as SVG. These photos are a clear holder with a dashed border and no baked shadow, so
    // the artwork box is the badge: trim to it with a 1px margin. The card-edge detector
    // that used to sit here was built to strip the old Badge Buddy photos' one-sided shadow;
    // on this artwork it read the dashed border as shadow and cut 5px off the left edge of
    // Badge Bottoms Horizontal and 2px off the top of Vertical.
    var card = { minX: minX, maxX: maxX, minY: minY, maxY: maxY };
    var visMinX = Math.max(0, minX - 1), visMaxX = Math.min(w - 1, maxX + 1);
    var visMinY = Math.max(0, minY - 1), visMaxY = Math.min(h - 1, maxY + 1);

    // Size #tab2 so the artwork - not the image canvas - spans the pane. Without this
    // the badge is narrower than its own box by however much transparent padding the
    // file carries, and that slack shows up as extra space either side however tightly
    // the edges are trimmed.
    var pane = tab2.parentNode;
    var paneCS = getComputedStyle(pane);
    var paneContent = pane.getBoundingClientRect().width -
      parseFloat(paneCS.paddingLeft) - parseFloat(paneCS.paddingRight);
    var visWidth = visMaxX - visMinX + 1;
    // The pane is a fixed frame now (same proportions on every product, custom-bb.css),
    // so a tall vertical artwork fits the frame's HEIGHT rather than its width - or it
    // ran to 1400px tall (Patrick, 2026-10-06).
    var paneContentH = pane.getBoundingClientRect().height -
      parseFloat(paneCS.paddingTop) - parseFloat(paneCS.paddingBottom);
    var visHeight = visMaxY - visMinY + 1;
    var targetVis = paneContent;
    if (paneContentH > 0 && visHeight > 0) targetVis = Math.min(paneContent, paneContentH * visWidth / visHeight);
    if (visWidth > 0 && targetVis > 0) {
      tab2.style.setProperty('width', (targetVis * w / visWidth).toFixed(1) + 'px', 'important');
    }

    var box = tab2.getBoundingClientRect();
    var scale = box.width / w;
    var deadLeft = Math.max(0, visMinX) * scale;
    var deadRight = Math.max(0, w - 1 - visMaxX) * scale;
    // The crop already trimmed the bottom, so only what is still inside the box counts.
    var visibleBottom = Math.min(h - 1, Math.round(box.height / scale) - 1);
    var deadTop = Math.max(0, visMinY) * scale;
    var deadBottom = Math.max(0, (visibleBottom - visMaxY)) * scale;

    // Trim the excess rather than pad the short side: padding the left to match the
    // right would only push the badge around inside a box that is too big. Each edge is
    // clipped back to the padded card box, so the visible badge is the box.
    //
    // clip-path does the trimming and a matching negative margin pulls the layout in
    // by the same amount, so the flex centring sees the trimmed size. #tab2's own
    // width is untouched, which matters - the colour plate's offsets are percentages
    // of it, so resizing the element would move the plate off the badge.
    var KEEP = 0;
    var t = Math.max(0, deadTop - KEEP);
    var rgt = Math.max(0, deadRight - KEEP);
    var btm = Math.max(0, deadBottom - KEEP);
    var lft = Math.max(0, deadLeft - KEEP);

    tab2.style.clipPath = 'inset(' + t.toFixed(1) + 'px ' + rgt.toFixed(1) + 'px ' +
      btm.toFixed(1) + 'px ' + lft.toFixed(1) + 'px)';
    tab2.style.marginTop = (-t).toFixed(1) + 'px';
    tab2.style.marginRight = (-rgt).toFixed(1) + 'px';
    tab2.style.marginBottom = (-btm).toFixed(1) + 'px';
    tab2.style.marginLeft = (-lft).toFixed(1) + 'px';

    // Find the slot: the grey band across the top fifth of the card. Used so the
    // watermark clears the slot this artwork actually draws, not the one the die
    // specifies - they are not in the same place.
    var slotBottomFrac = null;
    var cardH = card.maxY - card.minY + 1;
    var scanTo = card.minY + Math.round(cardH * 0.2);
    for (var sy = card.minY; sy < scanTo; sy++) {
      var greyRun = 0;
      for (var sx = card.minX; sx <= card.maxX; sx++) {
        var si = (sy * w + sx) * 4;
        if (d[si + 3] > 200 && d[si] > 190 && d[si] < 225 &&
            Math.abs(d[si] - d[si + 1]) < 12 && Math.abs(d[si] - d[si + 2]) < 12) greyRun++;
      }
      if (greyRun > 8) slotBottomFrac = (sy - card.minY) / cardH;
    }

    // The card box is now known, so the plate and watermark are placed by die fractions
    // against the card - the rectangle the die actually describes. Resolving them against
    // card-plus-shadow made the plate 7% too wide, leaving it all but touching the card's
    // edges instead of sitting inside them the way the production renderer draws it.
    applyDieGeometry({
      xFrac: card.minX / w,
      yFrac: card.minY / h,
      wFrac: (card.maxX - card.minX + 1) / w,
      hFrac: cardH / h,
      slotBottomFrac: slotBottomFrac
    });
  };
  probe.src = src;
}

function startBadgeFit() {
  // SPID-185 Phase 2: the stage no longer lives inside .bb-editor (that two-pane
  // wrapper is retired - see snippets/bb-stage.liquid). #tab2 itself is the right
  // guard: it exists on every Badge Buddy / Badge Bottoms page, whichever snippet
  // put it there, and centreArtwork() already no-ops on the six die variants
  // (no img.selectedBadge to measure).
  if (!document.getElementById('tab2')) return;
  centreArtwork();

  var badgeImg = document.querySelector('#tab2 img.selectedBadge');
  if (badgeImg) {
    // The src changes on the <img> when a variant swaps the artwork.
    new MutationObserver(centreArtwork)
      .observe(badgeImg, { attributes: true, attributeFilter: ['src'] });
    badgeImg.addEventListener('load', centreArtwork);
  }
  window.addEventListener('resize', centreArtwork);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', startBadgeFit);
} else {
  startBadgeFit();
}

/* SPID-185 Phase 2: the thumbnail grid under the stage (snippets/bb-stage.liquid)
 * lets a shopper look at the product's own photos without losing their design.
 * "Your design" is the active tile on load; clicking a photo swaps the stage to
 * that photo, and typing a custom line or changing colour - both of which change
 * what the design view is showing - switches back automatically (changeColor()
 * and the .custom-line-1/2 input handlers below call bbShowDesignView()), so the
 * shopper is never left looking at a stale, unrelated photo. */
// Product video tile (Patrick, 2026-10-05): plays in the stage pane. Any other view
// removes the iframe, which is what stops playback.
function bbStopVideo(pane) {
  var view = pane && pane.querySelector('[data-bb-stage-video]');
  if (!view) return;
  view.innerHTML = '';
  view.hidden = true;
  pane.classList.remove('is-video');
}

function bbShowVideoView(thumb) {
  var pane = document.querySelector('.bb-stage-pane');
  var view = pane && pane.querySelector('[data-bb-stage-video]');
  var id = thumb.getAttribute('data-video-id');
  if (!pane || !view || !id) return;

  // The pane keeps the badge view's height (--bb-pane-h, bbTrackPaneHeight).
  pane.classList.remove('is-photo');
  view.innerHTML = '<iframe src="https://www.youtube-nocookie.com/embed/' + encodeURIComponent(id) +
    '?autoplay=1&rel=0" title="Product video" frameborder="0" allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture" allowfullscreen></iframe>';
  view.hidden = false;
  pane.classList.add('is-video');

  var thumbs = document.querySelector('[data-bb-thumbs]');
  if (thumbs) {
    var active = thumbs.querySelector('.is-active');
    if (active) active.classList.remove('is-active');
  }
  thumb.classList.add('is-active');
}

function bbShowDesignView() {
  var pane = document.querySelector('.bb-stage-pane');
  if (!pane) return;
  pane.classList.remove('is-photo');
  bbStopVideo(pane);

  var thumbs = document.querySelector('[data-bb-thumbs]');
  if (!thumbs) return;
  var active = thumbs.querySelector('.is-active');
  if (active) active.classList.remove('is-active');
  var designThumb = thumbs.querySelector('[data-bb-thumb-design]');
  if (designThumb) designThumb.classList.add('is-active');
}

function bbShowPhotoView(thumb) {
  var pane = document.querySelector('.bb-stage-pane');
  var photo = pane && pane.querySelector('[data-bb-stage-photo]');
  var src = thumb.getAttribute('data-full');
  if (!pane || !photo || !src) return;

  bbStopVideo(pane);
  photo.src = src;
  photo.hidden = false;
  pane.classList.add('is-photo');

  var thumbs = document.querySelector('[data-bb-thumbs]');
  if (thumbs) {
    var active = thumbs.querySelector('.is-active');
    if (active) active.classList.remove('is-active');
  }
  thumb.classList.add('is-active');
}

// One box size for every view (Patrick, 2026-10-05: "the box changes sizes ... so it
// doesn't bounce"). The pane's height while it shows the badge is kept in --bb-pane-h;
// photos and the video then fill a box of exactly that height (custom-bb.css). Re-measured
// whenever the badge view itself resizes (window width, die change).
function bbTrackPaneHeight() {
  var pane = document.querySelector('.bb-stage-pane');
  var tab2 = document.getElementById('tab2');
  if (!pane || !tab2 || !window.ResizeObserver) return;
  var measure = function () {
    if (pane.classList.contains('is-photo') || pane.classList.contains('is-video')) return;
    var h = pane.offsetHeight;
    if (h > 0) pane.style.setProperty('--bb-pane-h', h + 'px');
  };
  new ResizeObserver(measure).observe(tab2);
  measure();
}

// Badge Bottoms (photo path, textfill): re-fit the text whenever the colour plate changes
// size. The plate is re-measured after the artwork loads and again as the page layout
// settles (the >=990px grid in custom-bb.css), and a fit made against an earlier, smaller
// plate left the default text at 27px against live's 60px until the shopper typed
// (Patrick, 2026-10-06: "your text is coming in really small").
function bbRefitOnPlateResize() {
  var tab2 = document.getElementById('tab2');
  var plate = tab2 && tab2.querySelector('.selectedColor');
  if (!plate || tab2.querySelector('[data-bb-svg]') || !window.ResizeObserver) return;
  var last = 0, queued = false;
  new ResizeObserver(function () {
    var w = Math.round(plate.getBoundingClientRect().width);
    if (!w || w === last || queued) return;
    last = w;
    queued = true;
    requestAnimationFrame(function () {
      queued = false;
      if (typeof window.bbSyncFromFields === 'function') window.bbSyncFromFields();
    });
  }).observe(plate);
}

// The thumbnail row scrolls sideways; the arrows page it by its visible width and hide
// at either end (and entirely when everything fits).
function bbStartThumbArrows(thumbs) {
  var rail = thumbs.closest('.bb-thumbs-rail');
  var prev = rail && rail.querySelector('[data-bb-thumbs-prev]');
  var next = rail && rail.querySelector('[data-bb-thumbs-next]');
  if (!prev || !next) return;
  function sync() {
    var max = thumbs.scrollWidth - thumbs.clientWidth;
    prev.hidden = thumbs.scrollLeft <= 2;
    next.hidden = thumbs.scrollLeft >= max - 2;
  }
  prev.addEventListener('click', function () { thumbs.scrollBy({ left: -thumbs.clientWidth * 0.85, behavior: 'smooth' }); });
  next.addEventListener('click', function () { thumbs.scrollBy({ left: thumbs.clientWidth * 0.85, behavior: 'smooth' }); });
  thumbs.addEventListener('scroll', sync, { passive: true });
  if (window.ResizeObserver) new ResizeObserver(sync).observe(thumbs);
  sync();
}

function startBadgeThumbs() {
  bbTrackPaneHeight();
  bbRefitOnPlateResize();
  var thumbs = document.querySelector('[data-bb-thumbs]');
  if (!thumbs) return;
  bbStartThumbArrows(thumbs);
  thumbs.addEventListener('click', function (e) {
    var thumb = e.target.closest('[data-bb-thumb-photo], [data-bb-thumb-design], [data-bb-thumb-video]');
    if (!thumb) return;
    if (thumb.hasAttribute('data-bb-thumb-design')) bbShowDesignView();
    else if (thumb.hasAttribute('data-bb-thumb-video')) bbShowVideoView(thumb);
    else bbShowPhotoView(thumb);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', startBadgeThumbs);
} else {
  startBadgeThumbs();
}

/* SPID-282 Phase A: lazy-loads the front-side text-position editor
 * (assets/bb-advanced.js / assets/bb-advanced.css) the first time a shopper clicks
 * "Advanced designer" in Step 2 (snippets/bb-custom-text-fields.liquid,
 * bb_editor_available - die variants only). Neither asset is referenced anywhere
 * else on the page, so a shopper who never clicks the button never fetches either
 * one; window.BB_ADVANCED_ASSET_URLS is the two asset_url values Liquid alone can
 * resolve, written by that same snippet only when the button itself renders.
 *
 * window.bbApplyCustomLayout and window.bbAdvApplyBarStyle (see bbRenderReferenceText
 * above) are the only other seams the loaded editor needs - everything else it does
 * lives in its own files.
 */
function bbLoadAdvancedEditorAssets(onReady) {
  if (window.bbAdvOpenEditor) { onReady(); return; }
  var urls = window.BB_ADVANCED_ASSET_URLS;
  if (!urls) return;

  if (!document.querySelector('link[data-bb-adv-css]')) {
    var link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = urls.css;
    link.setAttribute('data-bb-adv-css', 'true');
    document.head.appendChild(link);
  }

  // The script tag may already be in flight from an earlier click (a shopper who
  // double-clicks, or clicks again after Done) - poll for the function it defines
  // rather than injecting a second <script>.
  var existingScript = document.querySelector('script[data-bb-adv-js]');
  if (existingScript) {
    (function waitForEditor(tries) {
      if (window.bbAdvOpenEditor) { onReady(); return; }
      if (tries > 100) return;
      setTimeout(function () { waitForEditor(tries + 1); }, 50);
    })(0);
    return;
  }

  var script = document.createElement('script');
  script.src = urls.js;
  script.setAttribute('data-bb-adv-js', 'true');
  script.onload = onReady;
  document.head.appendChild(script);
}

function startBadgeAdvancedEditorButton() {
  // Simplified designer (Patrick, 2026-10-05): no separate "advanced" mode any more -
  // bar style, fonts and logo are always in the card, so the editor loads and opens on
  // its own once the page is idle. BB_ADVANCED_ASSET_URLS only exists where the editor
  // is available (die variants), so Badge Bottoms never loads it.
  if (window.BB_ADVANCED_ASSET_URLS) {
    var bbAutoOpen = function () {
      bbLoadAdvancedEditorAssets(function () {
        if (window.bbAdvOpenEditor) window.bbAdvOpenEditor();
      });
    };
    if ('requestIdleCallback' in window) requestIdleCallback(bbAutoOpen, { timeout: 1500 });
    else setTimeout(bbAutoOpen, 300);
    // Safety net (Patrick, 2026-10-06: Bar style "a flip of the dice"): once the page
    // has fully loaded, open the designer again if its steps still have not mounted.
    var bbAutoOpenCheck = function () {
      setTimeout(function () {
        if (!document.querySelector('[data-bb-mount-barstyle] .bb-adv-bars, [data-bb-mount-logo] .bb-adv-logo')) bbAutoOpen();
      }, 1200);
    };
    if (document.readyState === 'complete') bbAutoOpenCheck();
    else window.addEventListener('load', bbAutoOpenCheck);
  }
  document.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-bb-open-editor]');
    if (!btn) return;
    e.preventDefault();
    bbLoadAdvancedEditorAssets(function () {
      if (window.bbAdvOpenEditor) window.bbAdvOpenEditor();
    });
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', startBadgeAdvancedEditorButton);
} else {
  startBadgeAdvancedEditorButton();
}

/* SPID-185 Phase 6 (2026-09-18): Simon - on phones the "Your text" step sits well
 * below the preview, past the volume tier row and the quantity/total row, so a
 * shopper has to scroll past several blocks before they see it. This moves the
 * single existing .bb-custom-text-card node (snippets/bb-custom-text-fields.liquid
 * - Line 1, Line 2, fit note, charset note) into the empty slot
 * snippets/bb-stage.liquid renders right under the stage note
 * ([data-bb-text-slot-mobile]), below Dawn's own 750px breakpoint, and back into
 * its home in the controls card (snippets/bb-controls-card.liquid's Step 2) at
 * 750px and up. Desktop is untouched.
 *
 * This moves the node itself - never clones or re-renders it - so there is still
 * only one #bb-custom-text on the page, and every existing handle to it keeps
 * working with no change: custom-bb-required.js's document.getElementById and
 * MutationObserver, and the $(document).on('input', '.custom-line-1 ...', ...)
 * handlers below are all delegated to the document or to a live id lookup, neither
 * of which cares where in the tree the node currently sits. Values on the <input>
 * elements survive the move for the same reason a drag-and-drop reorder would not
 * reset them - reparenting a node does not recreate it.
 *
 * Runs immediately (this script tag is `defer`, so the DOM already exists by the
 * time it executes) rather than waiting for DOMContentLoaded, to keep the field's
 * jump into place as close to first paint as possible. */
function bbMobileTextSlot() {
  var card = document.querySelector('.bb-controls .bb-custom-text-card');
  var slot = document.querySelector('[data-bb-text-slot-mobile]');
  var stepWrap = document.querySelector('.bb-controls__step[data-bb-text-step]');
  if (!card || !slot || !stepWrap) return;

  // Captured once, before any move, so "back to desktop" always means the exact
  // node the fields started in - not wherever bb-mobile-text-slot last was.
  var homeParent = card.parentNode;
  var mq = window.matchMedia('(max-width: 749px)');

  function apply(isMobile) {
    if (isMobile) {
      if (card.parentNode !== slot) slot.appendChild(card);
      stepWrap.classList.add('bb-controls__step--relocated');
    } else {
      if (card.parentNode !== homeParent) homeParent.appendChild(card);
      stepWrap.classList.remove('bb-controls__step--relocated');
    }
  }

  apply(mq.matches);
  if (mq.addEventListener) {
    mq.addEventListener('change', function (e) { apply(e.matches); });
  } else if (mq.addListener) {
    // Older WebKit (still in play on some in-app browsers) never picked up
    // MediaQueryList.addEventListener.
    mq.addListener(function (e) { apply(e.matches); });
  }
}
bbMobileTextSlot();

/* Floating mini preview on phones (Patrick, 2026-10-06): colour, bar style and logo sit
 * well below the badge on a phone, so once the badge scrolls out of view while the
 * shopper is still in the designer, a small live copy of it floats under the header.
 * It is a scaled clone of #tab2, refreshed on any change to the stage, so it shows
 * exactly what the big preview shows (SVG dies and the BadgeBottoms photo plate
 * alike). Tapping it scrolls back to the full preview. Phones only. */
function bbMiniPreview() {
  var stage = document.getElementById('tab2');
  var pane = document.querySelector('.bb-stage-pane');
  var info = document.querySelector('.product__info-container');
  if (!stage || !pane || !info || document.querySelector('.bb-mini')) return;
  var product = stage.closest('.product');
  var mq = window.matchMedia('(max-width: 749px)');
  var MINI_H = 96;

  var mini = document.createElement('button');
  mini.type = 'button';
  mini.className = 'bb-mini';
  mini.setAttribute('aria-label', 'Your design. Tap to see the full preview.');
  mini.tabIndex = -1;
  var frame = document.createElement('div');
  frame.className = 'bb-mini__frame';
  var label = document.createElement('span');
  label.className = 'bb-mini__label';
  label.textContent = 'Your design';
  mini.appendChild(frame);
  mini.appendChild(label);
  document.body.appendChild(mini);

  var shown = false, dirty = true, queued = false;

  function headerBottom() {
    var h = document.querySelector('sticky-header, .section-header');
    var b = h ? h.getBoundingClientRect().bottom : 0;
    return b > 0 ? b : 0;
  }

  function snap() {
    if (!shown || !dirty) return;
    // The colour table's selectors are .product.product-variant-color-<slug>, so the
    // frame carries the product's colour class (the BadgeBottoms plate needs it).
    var colors = product ? Array.prototype.filter.call(product.classList, function (c) {
      return c.indexOf('product-variant-color-') === 0;
    }) : [];
    frame.className = 'bb-mini__frame product ' + colors.join(' ');
    var svg = stage.querySelector('[data-bb-svg]');
    var c, fw;
    if (svg) {
      // Die variants: just the badge SVG, sized from its viewBox, so nothing is cropped.
      var vb = (svg.getAttribute('viewBox') || '0 0 1 1').split(/[\s,]+/).map(Number);
      fw = Math.round(MINI_H * vb[2] / vb[3]);
      c = svg.cloneNode(true);
      c.removeAttribute('data-bb-svg');
      c.setAttribute('width', fw);
      c.setAttribute('height', MINI_H);
      c.style.cssText = 'display:block;width:' + fw + 'px;height:' + MINI_H + 'px;';
    } else {
      // BadgeBottoms: a cropped photo plate. Copy the stage with its own crop
      // (clip-path inset + matching negative margins put the visible part at the
      // origin) and scale the visible box. It keeps its id so the #tab2 rules apply;
      // the original comes first in the document, so getElementById still finds it.
      var w = stage.offsetWidth, h = stage.offsetHeight;
      if (!w || !h) return;
      var m = /inset\(([^)]*)\)/.exec(getComputedStyle(stage).clipPath || '');
      var ins = m ? m[1].split(/\s+/).map(parseFloat) : [0, 0, 0, 0];
      while (ins.length < 4) ins.push(ins[ins.length === 1 ? 0 : ins.length - 2]);
      var vw = w - ins[1] - ins[3], vh = h - ins[0] - ins[2];
      if (!(vw > 0 && vh > 0)) return;
      var k = MINI_H / vh;
      fw = Math.round(vw * k);
      var inner = document.createElement('div');
      inner.style.cssText = 'position:absolute;left:0;top:0;width:' + vw + 'px;height:' + vh + 'px;transform:scale(' + k + ');transform-origin:0 0;';
      var t = stage.cloneNode(true);
      t.setAttribute('aria-hidden', 'true');
      t.style.pointerEvents = 'none';
      inner.appendChild(t);
      c = inner;
    }
    dirty = false;
    frame.style.width = fw + 'px';
    frame.style.height = MINI_H + 'px';
    while (frame.firstChild) frame.removeChild(frame.firstChild);
    frame.appendChild(c);
  }

  function check() {
    queued = false;
    var hb = headerBottom();
    var on = mq.matches &&
      pane.getBoundingClientRect().bottom < hb + 40 &&
      info.getBoundingClientRect().bottom > hb + MINI_H + 60;
    if (on !== shown) {
      shown = on;
      mini.classList.toggle('is-on', on);
      mini.tabIndex = on ? 0 : -1;
    }
    if (on) {
      mini.style.top = Math.round(hb + 8) + 'px';
      snap();
    }
  }
  function queue() { if (!queued) { queued = true; requestAnimationFrame(check); } }

  new MutationObserver(function () { dirty = true; if (shown) queue(); })
    .observe(stage, { subtree: true, childList: true, attributes: true, characterData: true });
  if (product) {
    new MutationObserver(function () { dirty = true; if (shown) queue(); })
      .observe(product, { attributes: true, attributeFilter: ['class'] });
  }
  window.addEventListener('scroll', queue, { passive: true });
  window.addEventListener('resize', function () { dirty = true; queue(); });

  mini.addEventListener('click', function () {
    var top = pane.getBoundingClientRect().top + window.pageYOffset - headerBottom() - 12;
    window.scrollTo({ top: top, behavior: 'smooth' });
  });
  queue();
}
bbMiniPreview();

var lineNumber = 1;
var badgeText = "";
var badgeText2 = "";
var colorName = "Blue";

function getParameterByName(name, url) {
    if (!url) url = window.location.href;
    name = name.replace(/[\[\]]/g, '\\$&');
    var regex = new RegExp('[?&]' + name + '(=([^&#]*)|&|#|$)'),
        results = regex.exec(url);
    if (!results) return null;
    if (!results[2]) return '';
    return decodeURIComponent(results[2].replace(/\+/g, ' '));
}

function sanitizeText(text) {
    var div = document.createElement('div');
    div.appendChild(document.createTextNode(text));
    return div.innerHTML;
}

// Skips the first src write when Liquid has already server-rendered the
// preview image (flagged via data-ssr-rendered). Prevents a double-fetch
// of the LCP image on initial page load; subsequent variant changes work
// normally because the flag is removed after the first skip.
function setSelectedBadgeSrc(url) {
  var $img = $(".selectedBadge");
  if ($img.attr("data-ssr-rendered") === "true") {
    $img.removeAttr("data-ssr-rendered");
    return;
  }
  $img.attr("src", url);
}

/**
 * Colour plate geometry, expressed as percentages of #tab2's width rather than
 * fixed pixels.
 *
 * The plate has to stay locked to the badge artwork underneath it, and the old
 * pixel offsets only held while #tab2 stayed exactly 281px wide - resize the
 * preview and the plate slid off the badge. Percentages of the container width
 * track the artwork, because the image is width:100%/height:auto, so its rendered
 * height scales with that same width.
 *
 * Note margin-top percentages resolve against the containing block's WIDTH, not
 * its height. That is what makes the vertical offset scale in step with the image.
 *
 * The height comes from aspect-ratio rather than a percentage: a percentage height
 * resolves against #tab2's fixed 400px, which would stop tracking the image the
 * moment the preview is resized. Each ratio is the original pixel width/height
 * pair, so the plate keeps exactly the shape it had.
 *
 * Only the two Badge Bottoms call sites still reach these percentages - every other
 * caller has a die profile, so the guard below returns before any of this runs. The
 * XL vertical badge buddy used to be one of them (percentages of a 401px #tab2, via
 * a now-removed .vertical-badge-buddies-1xl width override), back when it was a
 * photo like Badge Bottoms; SPID-185 gave it a die profile and it draws as SVG now.
 *
 * The two live values are duplicated in snippets/bb-stage.liquid's
 * badge_preview_overlay_style / badge_preview_crop assigns, which render the same
 * plate server-side for the first paint. Change both together.
 */
function setColorPlate(width, marginLeft, marginTop, ratio, previewCrop) {
  // Photo-era plate and crop placement. Where a die profile exists the badge is drawn
  // as SVG (bb-stage-svg.liquid, bbRenderReferenceText) and none of this applies - #tab2
  // has no image to crop and .selectedColor is a hidden colour-lookup element, not the
  // plate, so writing a percentage width/aspect-ratio onto either would only fight the
  // stage sizing rule in custom-bb.css. select*() runs this on every colour change
  // regardless of variant, so the guard has to live here rather than at each call site.
  if (bbDieProfile()) return;

  // These percentages predate the die geometry and place the plate against the image
  // canvas, which leaves 19px of card showing on the left against 15px on the right.
  $(".selectedColor").css({
    width: width,
    "margin-left": marginLeft,
    "margin-top": marginTop,
    height: "auto",
    "aspect-ratio": ratio
  });

  // Crops the transparent padding below the badge artwork. The ratio is the image's
  // own width over the row where its artwork ends, measured off each file rather than
  // guessed, so #tab2 stops exactly at the badge. custom-bb.css supplies the
  // overflow:hidden and height:auto this needs.
  $("#tab2").css({ "aspect-ratio": previewCrop, height: "auto" });

  // Re-measure and re-place. The image may already be cached, in which case swapping the
  // src fires no load event and nothing else would reapply the geometry.
  if (typeof centreArtwork === 'function') centreArtwork();
}

function selectVerticalBadgeBottom() {
  setColorPlate("45.9075%", "24.1993%", "-43.0605%", "129 / 86", "281 / 376");
  setSelectedBadgeSrc("//cdn.shopify.com/s/files/1/1260/4809/files/bbottomvertical.webp?v=1689698730");

  $("#imgHorizontal").css("border", "0px");
  $(".horimg").css("display", "none");
  $("#imgVertical").css("border", "3px solid #F5911F");
  wide = false;
  $(".product_code").text("BADGEBOTTOMS-V");
  productCode = "BADGEBOTTOMS-V";
  fixTextPosition(1);
  resetText();
}

function selectHorizontalBadgeBottom() {
  setColorPlate("67.6157%", "16.0142%", "-32.0285%", "190 / 61", "300 / 284");
  $("#tab1").css({"margin-bottom": "-100px"});
  setSelectedBadgeSrc("//cdn.shopify.com/s/files/1/1260/4809/files/bbottomhorizon.jpg?v=1689699832");

  $(".verimg").css("display", "none");
  $("#imgHorizontal").css("border", "3px solid #F5911F");

  $("#imgVertical").css("border", "0px");
  $(".product_code").text("BADGEBOTTOMS-H");
  productCode = "BADGEBOTTOMS-H";
  wide = true;
  fixTextPosition(1);
  resetText();
}

function selectHorizontalBadgeBuddiesXL() {
  setColorPlate("65.1246%", "18.8612%", "-29.5374%", "183 / 61", "1200 / 1181");
  $("#tab1").css({"margin-bottom": "-100px"});
  setSelectedBadgeSrc('//cdn.shopify.com/s/files/1/1260/4809/products/BB-1XL-CUSTOM-BLUE-H.png?v=1688152738');

  $(".verimg").css("display", "none");
  $("#imgHorizontal").css("border", "3px solid #F5911F");

  $("#imgVertical").css("border", "0px");
  $(".product_code").text("BADGEBOTTOMS-H-1XL");
  productCode = "BADGEBOTTOMS-H-1XL";
  wide = true;
  fixTextPosition(1);
  resetText();
}

function selectVerticalBadgeBuddiesXL() {
  setColorPlate("30.4239%", "35.6608%", "-28.9277%", "122 / 84", "800 / 763");
  $("#tab1").css({"margin-bottom": "-100px"});
  setSelectedBadgeSrc('//www.specialistid.com/cdn/shop/t/68/assets/BB-1XL-CUSTOM-RED-V_800x800.png?v=136908169347785232851690782186');

  $(".verimg").css("display", "none");
  $("#imgVertical").css("border", "3px solid #F5911F");

  $("#imgVertical").css("border", "0px");
  $(".product_code").text("BADGEBOTTOMS-H-1XL");
  productCode = "BADGEBOTTOMS-H-1XL";
  wide = true;
  fixTextPosition(1);
  resetText();
}

function selectHorizontalClear() {
  setColorPlate("86.8327%", "3.5587%", "-80.4270%", "244 / 88", "281 / 271");

  setSelectedBadgeSrc("//cdn.shopify.com/s/files/1/1260/4809/files/bbhorizontal.webp?v=1689698297");
  $(".selectedBadge").css({"pointer-events": "none"});
  $(".textHolder").css({"pointer-events": "auto"});

  $("#imgHorizontal").css("border", "3px solid #F5911F");
  $("#imgVertical").css("border", "0px");
  $(".product_code").text("BB-CUSTOM-COLOR-H");
  productCode = "BB-CUSTOM-COLOR-H";
  wide = true;
  fixTextPosition(1);
  resetText();
}

function selectVerticalClear() {
  setColorPlate("51.9573%", "21.3523%", "-48.3986%", "146 / 95", "281 / 368");

  setSelectedBadgeSrc("//cdn.shopify.com/s/files/1/1260/4809/files/bbvertical.webp?v=1689698740");
  $("#imgHorizontal").css("border", "0px");
  $("#imgVertical").css("border", "3px solid #F5911F");
  wide = false;
  $(".product_code").text("BB-CUSTOM-COLOR-V");
  productCode = "BB-CUSTOM-COLOR-V";
  fixTextPosition(1);
  resetText();
}

function selectHorizontal() {
  setColorPlate("80.0712%", "7.1174%", "-80.4270%", "225 / 78", "281 / 271");

  setSelectedBadgeSrc("//cdn.shopify.com/s/files/1/1260/4809/files/bbhorizontal.webp?v=1689698297");
  $(".selectedBadge").css({"pointer-events": "none"});
  $(".textHolder").css({"pointer-events": "auto"});

  $("#imgHorizontal").css("border", "3px solid #F5911F");
  $("#imgVertical").css("border", "0px");
  $(".product_code").text("BB-CUSTOM-COLOR-H");
  productCode = "BB-CUSTOM-COLOR-H";
  wide = true;
  fixTextPosition(1);
  resetText();
}

function selectVertical() {
  setColorPlate("45.9075%", "24.9110%", "-48.3986%", "129 / 86", "281 / 368");

  setSelectedBadgeSrc("//cdn.shopify.com/s/files/1/1260/4809/files/bbvertical.webp?v=1689698740");
  $("#imgHorizontal").css("border", "0px");
  $("#imgVertical").css("border", "3px solid #F5911F");
  wide = false;
  $(".product_code").text("BB-CUSTOM-COLOR-V");
  productCode = "BB-CUSTOM-COLOR-V";
  fixTextPosition(1);
  resetText();
}

function resetApp() {
  $("#inputQuntity").val("6");

  var custom_bb_type = $(".custom_bb_type").val();

  switch (custom_bb_type) {
    case 'Badge Buddy - Horizontal':
      selectHorizontal();
      break;
    case 'Badge Buddy - Vertical':
      selectVertical();
      break;
    case 'Badge Buddy Clear - Horizontal':
      selectHorizontalClear();
      break;
    case 'Badge Buddy Clear - Vertical':
      selectVerticalClear();
      break;
    case 'Badge Buddy - XL - Horizontal':
      selectHorizontalBadgeBuddiesXL();
      break;
    case 'Badge Buddy - XL - Vertical':
      selectVerticalBadgeBuddiesXL();
      break;
    case 'Badge Bottoms - Horizontal':
      selectHorizontalBadgeBottom();
      break;
    case 'Badge Bottoms - Vertical':
      selectVerticalBadgeBottom();
      break;
  }

  resetText();
}

function resetText(){
    $("#inputText1").val("");
    $("#badgeOneLine").val("");

    $("#inputText2").val("");
    $("#badgeSecondLine").val("");

    setBadgeText('');
    setBadgeText('',true);
}

function fixTextPosition(tab){
	// The SVG text layer is positioned by the ported geometry, not by these classes, and
	// the variant selectors call this on every change - so leave it alone.
	if (bbDieProfile()) return;

	if(wide){
		$('#badge-preview-text').attr( "class", "badge-preview-text-tab"+tab +"w");
	}else{
		$('#badge-preview-text').attr( "class", "badge-preview-text-tab"+tab );
	}

	if(lineNumber==2){
		$('#badge-preview-text').attr( "class", $('#badge-preview-text').attr( "class") + "line2");
	}
}

// Sets the Badge Bottoms title spans at the standard bar's point sizes. The plate
// (.selectedColor) stands for the printed title bar, so its rendered width maps to the
// profile's sizingWidth in inches. Returns false (and leaves textfill to run) when this
// is not a Badge Bottoms stage or the plate has no width yet.
function bbBottomsSizing() {
  var tab2 = document.getElementById('tab2');
  var orient = tab2 && tab2.getAttribute('data-bb-orient');
  var profile = orient && BB_FONT_PROFILES[orient];
  var plate = tab2 && tab2.querySelector('.selectedColor');
  var box = document.getElementById('badge-preview-text');
  if (!profile || !plate || !box || tab2.querySelector('[data-bb-svg]')) return false;
  var pr = plate.getBoundingClientRect();
  var plateW = pr.width, plateH = pr.height;
  if (!(plateW > 0) || !(plateH > 0)) return false;
  var sizing = bbCalculateFontSizes(orient, badgeText, badgeText2);
  if (!sizing) return false;

  // Point sizes from the standard bar's production rules, mapped onto the plate. Then
  // (Patrick, 2026-10-06) at least 2mm clear on every side, and the line(s) centred on
  // the letters themselves: caps sit 0.66em tall, and an HTML line box would otherwise
  // ride the capitals high in the pocket.
  var pxPerPt = plateW / bbInches(profile.sizingWidth);
  var margin = 2 / 25.4 * BB_POINTS_PER_INCH * pxPerPt;
  var CAP = 0.66, ASCENT = 0.84;
  var gapPt = bbInches(orient === 'V' ? 0.109 : 0.059);
  var lines = [];
  if (sizing.line1) lines.push({ sel: sizing.line2 ? '#doubleSpan1' : '#singleSpan', text: sizing.line1, pt: sizing.fontSize1 });
  if (sizing.line2) lines.push({ sel: sizing.line1 ? '#doubleSpan2' : '#doubleSpan2', text: sizing.line2, pt: sizing.fontSize2 });
  if (!lines.length) { bbLastSizing = sizing; return true; }

  var gap = lines.length > 1 ? gapPt * pxPerPt : 0;
  var maxW = 0, blockH = gap;
  lines.forEach(function (l) {
    l.px = l.pt * pxPerPt;
    l.w = bbMeasureTextPoints(l.text, l.pt) * pxPerPt;
    maxW = Math.max(maxW, l.w);
    blockH += CAP * l.px;
  });
  var scale = Math.min(1, (plateW - 2 * margin) / maxW, (plateH - 2 * margin) / blockH);
  gap *= scale; blockH *= scale;

  box.style.top = '0'; box.style.left = '0'; box.style.right = '0'; box.style.bottom = '0';
  box.style.width = 'auto'; box.style.padding = '0';
  var y = (plateH - blockH) / 2;
  lines.forEach(function (l, i) {
    var el = document.querySelector(l.sel);
    if (!el) return;
    var px = l.px * scale;
    var baseline = y + CAP * px;
    el.style.position = 'absolute';
    el.style.left = '50%';
    el.style.transform = 'translateX(-50%)';
    el.style.fontSize = px.toFixed(2) + 'px';
    el.style.lineHeight = '1';
    el.style.whiteSpace = 'nowrap';
    el.style.margin = '0';
    el.style.top = (baseline - ASCENT * px).toFixed(2) + 'px';
    y = baseline + gap;
    l.el = el; l.pxSet = px;
  });
  // The point sizes come from Minion Pro's width table, but most browsers draw the
  // fallback serif (Times), which runs wider - so check the width actually drawn and
  // shrink every line by the same factor if one would cross the 2mm margin.
  var drawnMax = 0;
  lines.forEach(function (l) { if (l.el) drawnMax = Math.max(drawnMax, l.el.getBoundingClientRect().width); });
  var fix = drawnMax > 0 ? Math.min(1, (plateW - 2 * margin) / drawnMax) : 1;
  if (fix < 0.999) {
    var block2 = gap * fix;
    lines.forEach(function (l) { block2 += CAP * l.pxSet * fix; });
    var y2 = (plateH - block2) / 2;
    lines.forEach(function (l) {
      if (!l.el) return;
      var px2 = l.pxSet * fix;
      var base2 = y2 + CAP * px2;
      l.el.style.fontSize = px2.toFixed(2) + 'px';
      l.el.style.top = (base2 - ASCENT * px2).toFixed(2) + 'px';
      y2 = base2 + gap * fix;
    });
  }
  bbLastSizing = sizing;
  return true;
}

function setBadgeText(txt, secondLine) {
    if (secondLine) {
        badgeText2 = txt;
    } else {
        badgeText = txt;
    }

    // Determine line count based on whether line 2 actually has content
    var line2Val = $(".custom-line-2 input[type=text]").val() || "";
    var hasSecondLine = (badgeText2 && badgeText2 !== "") || (line2Val !== "");
	if(hasSecondLine){
		lineNumber = 2;
	}else{
		lineNumber = 1;
	}
    if(badgeText == undefined){
    	badgeText = "";
    }

    if(badgeText2 == undefined){
    	badgeText2 = "";
    }

    // Badge Buddies use the ported production sizing; Badge Bottoms, which the renderer
    // has no profile for, fall through to textfill below.
    if (bbRenderReferenceText()) {
        updateFieldFeedback();
        return;
    }
    bbLastSizing = null;

    var html;

    if (lineNumber == 2 && secondLine) {
    	html = '<div id="txtSpan1" style="width:100%;"><span id="doubleSpan1" style="display:inline-block">' + sanitizeText(badgeText) + '</span></div>';
    	html += '<div id="txtSpan2" style="width:100%;"><span id="doubleSpan2" style="display:inline-block">' + sanitizeText(txt) + '</span></div>';
    } else if (lineNumber == 2) {
    	html = '<div id="txtSpan1" style="width:100%;"><span id="doubleSpan1" style="display:inline-block">' + sanitizeText(txt) +'</span></div>';
        html += '<div id="txtSpan2" style="width:100%;"><span id="doubleSpan2" style="display:inline-block">'+ sanitizeText(badgeText2) + '</span></div>';
    } else {
        html = "<div id='txtSingle' style='width:100%;'><span id='singleSpan' style='display:inline-block'>" + sanitizeText(badgeText) + '</span></div>';
    }

    $("#badge-preview-text").html(html);

    // Badge Bottoms (Patrick, 2026-10-06): "if I type RN ... that's not at all how we would
    // print it. It would print the same way that we print with the standard badge buddy
    // bar." So the title is sized by the standard bar's production rules for this
    // orientation (bbCalculateFontSizes - same font table, caps and title height) and
    // drawn at that point size, scaled to the plate. textfill, which stretched any text to
    // fill the plate's width, is only the fallback now.
    if (bbBottomsSizing()) {
        updateFieldFeedback();
        return;
    }

    if ($.fn.textfill) {
      $("#txtSpan1").textfill({
          widthOnly: true,
          maxFontPixels:40
      });

      $("#txtSpan2").textfill({
          widthOnly: true,
          maxFontPixels:40
      });

      $("#txtSingle").textfill({
          widthOnly: true,
          maxFontPixels:60
      });
    }

    // Match line-height to font-size (ratio 0.733 based on minion-pro: 44px line-height at 60px font-size)
    var lhRatio = 0.733;

    if(lineNumber == 1){
        var singleSize = parseFloat($("#singleSpan").css("font-size")) || 0;
        if(singleSize > 0){
            $("#singleSpan").css("line-height", Math.ceil(singleSize * lhRatio) + "px");
        }
    }

    if(lineNumber == 2){
        var size1 = parseFloat($("#doubleSpan1").css("font-size")) || 0;
        var size2 = parseFloat($("#doubleSpan2").css("font-size")) || 0;

        // Equalize font sizes between the two lines for consistent look
        if(size1 > 0 && size2 > 0){
            var minSize = Math.min(size1, size2);
            $("#doubleSpan1").css("font-size", minSize + "px");
            $("#doubleSpan2").css("font-size", minSize + "px");
            size1 = minSize;
            size2 = minSize;
        }

        if(size1 > 0){
            $("#doubleSpan1").css("line-height", Math.ceil(size1 * lhRatio) + "px");
        }
        if(size2 > 0){
            $("#doubleSpan2").css("line-height", Math.ceil(size2 * lhRatio) + "px");
        }
    }

    // Add bottom margin when descender characters are present (proportional to font size)
    var descenderRatio = 0.2;
    if(lineNumber == 1){
        var singleFontSize = parseFloat($("#singleSpan").css("font-size")) || 0;
        $("#singleSpan").css("margin-bottom", checkForDescenders(badgeText) ? Math.ceil(singleFontSize * descenderRatio) + "px" : "0px");
    }
    if(lineNumber == 2){
        var font1 = parseFloat($("#doubleSpan1").css("font-size")) || 0;
        var font2 = parseFloat($("#doubleSpan2").css("font-size")) || 0;
        $("#doubleSpan1").css("margin-bottom", checkForDescenders(badgeText) ? Math.ceil(font1 * descenderRatio) + "px" : "0px");
        $("#doubleSpan2").css("margin-bottom", checkForDescenders(badgeText2) ? Math.ceil(font2 * descenderRatio) + "px" : "0px");
    }

    updateFieldFeedback();
}

// BB placement rules v1: the old advisory notes are gone - the "long line prints
// small" note (already removed from the page on 2026-10-06) and the faint "These
// characters may not print exactly as shown" note. Both cases now hold the order with a
// clear message next to the fields: text under production's minimum size, and a
// character the chosen font does not have (bbLayoutRefreshGate, [data-bb-layout-note]).
// Badge Bottoms set no layout issues, so this only ever hides the note for them.
function updateFieldFeedback() {
    bbLayoutRefreshGate();
}

function checkForDescenders(text) {
    if (!text) return false;
    return /[qypgj]/.test(text);
}

function cleanStyleValue(value){
	return Number(parseFloat(value));
}

// Returns the typed line as-is. It used to HTML-escape here, for the old photo-plate
// preview that wrote the text with .html() - but that escaped copy also became the
// text the SVG designer sized, drew (as textContent, so "&" showed as "&amp;") and
// wrote into _Design. Odin's QA, 2026-10-06: "L&D" displayed escaped in the preview,
// the proof and the order data. Escaping now happens only where text is joined into
// HTML (setBadgeText's legacy preview below), which is the one place that needs it.
function replaceKey(value){
    return value;
}

// Typographic punctuation a phone keyboard substitutes on its own - iOS "Smart
// Punctuation" turns ' into ’ and -- into — - mapped to the plain character
// that prints. Odin's QA, 2026-10-06: "O’Neil" typed on an iPhone was flagged
// unsupported while the preview and proof drew it normally, so a customer could
// approve a proof that does not match the print. Every substitution is one
// character for one, so a caret position stays valid across it.
var BB_TYPOGRAPHIC_SUBSTITUTES = {
    '‘': "'", '’': "'", '‚': "'", '‛': "'", '′': "'",
    '“': '"', '”': '"', '„': '"', '‟': '"', '″': '"',
    '–': '-', '—': '-', '−': '-'
};
function bbStraightenTypography(text) {
    return String(text == null ? '' : text).replace(/[‘’‚‛′“”„‟″–—−]/g,
        function (c) { return BB_TYPOGRAPHIC_SUBSTITUTES[c]; });
}

function changeColor(color) {
    if (!color) return;

    var $productContainer = $(".product").first();
    if ($productContainer.length === 0) {
        $productContainer = $("body");
    }

    $productContainer.removeClass(function (index, className) {
        return (className.match (/(^|\s)product-variant-color-\S+/g) || []).join(' ');
    });

    var colorClass = 'product-variant-color-' + color.replace(/\s+/g, '-').toLowerCase();
    $productContainer.addClass(colorClass);

    // Name the chosen colour beside the swatches. The element sits outside
    // <variant-selects> deliberately - product-info.js replaces that whole element on
    // every variant change - so it is ours to keep in step. See bb-controls-card.liquid.
    $('[data-bb-color-name]').text(color);

    // The SVG strip/title fill are read from .selectedColor's computed style, which the
    // class swap above just changed - re-render so a colour click actually recolours the
    // stage rather than waiting for the next keystroke.
    bbRenderReferenceText();

    // A colour change is a change to what "Your design" shows, so return to it if a
    // product-photo thumbnail was active.
    bbShowDesignView();
}

$(document).ready(function() {
    resetApp();

    // Detect initial color selection
    var startColor = null;
    $("input[type='radio']:checked").each(function() {
        var inputName = $(this).attr('name');
        if (inputName && inputName.toLowerCase().indexOf('color') !== -1) {
            startColor = $(this).val();
            return false;
        }
    });

    if (startColor) {
        changeColor(startColor);
    } else {
        $("input[type='radio']").each(function() {
            var inputName = $(this).attr('name');
            if (inputName && inputName.toLowerCase().indexOf('color') !== -1) {
                startColor = $(this).val();
                changeColor(startColor);
                return false;
            }
        });
    }

    // Straightens curly quotes and long dashes in the field itself, as they are
    // typed. Bound before the listeners below so every one of them - the preview,
    // the front/back properties - reads the corrected text, and what the customer
    // sees in the field is what prints.
    $(document).on('input', '.custom-line-1 input[type=text], .custom-line-2 input[type=text]', function() {
        var value = this.value, straight = bbStraightenTypography(value);
        if (straight === value) return;
        var start = this.selectionStart, end = this.selectionEnd;
        this.value = straight;
        try { this.setSelectionRange(start, end); } catch (e) { /* not focused */ }
    });

    // Text input listeners. Delegated from the document rather than from
    // #infiniteoptions-container so they bind either way: the fields are now
    // server-rendered by snippets/bb-custom-text-fields.liquid, but the same
    // .custom-line-N hooks still match if the Infinite Options app injects them.
    $(document).on('input', '.custom-line-1 input[type=text]', function() {
        setBadgeText(replaceKey($(this).val()));
        // On a different back, the fields edit the back's lines - the front's
        // properties[Custom Line 1/2] must keep the front text.
        if (window.bbCurrentSide === 'back') bbBackField('properties[Back Line 1]').value = $(this).val();
        else $("#bb_option_custom_line_1").val($(this).val());
        bbShowDesignView();
    });

    $(document).on('input', '.custom-line-2 input[type=text]', function() {
        setBadgeText(replaceKey($(this).val()), true);
        if (window.bbCurrentSide === 'back') bbBackField('properties[Back Line 2]').value = $(this).val();
        else $("#bb_option_custom_line_2").val($(this).val());
        bbShowDesignView();
    });

    // Single color change listener that handles all color-related radio inputs
    $(document).on('change', 'input[type="radio"]', function() {
        var inputName = $(this).attr('name');
        if (inputName && inputName.toLowerCase().indexOf('color') !== -1) {
            changeColor($(this).val());
        }
    });

    // Deep links carry the badge text as ?line1=/?line2=. Anything already in the
    // field wins - that is either the shopper typing or the cbb.custom_bb_text_1
    // metafield default rendered into the input - so this only fills a blank form.
    // Selectors are class-based, not name-based: the server-rendered fields carry
    // no `properties[...]` name (the hidden inputs in buy-buttons.liquid own that),
    // and the app-injected markup sits inside the same .custom-line-N wrappers.
    // One-shot (SPID-334): on live, the legacy observer below re-ran this on every DOM
    // change, so a shopper who cleared Line 1 got the deep-link text straight back
    // (Shopper Approved review, order 1175598). Once the fields exist, apply at most
    // once - an empty field after that is the shopper's choice.
    var queryParamsApplied = false;

    function applyQueryParams() {
        if (queryParamsApplied) return;

        var $line1 = $('.custom-line-1 input[type=text]');
        var $line2 = $('.custom-line-2 input[type=text]');
        if (!$line1.length) return; // legacy fields not injected yet
        queryParamsApplied = true;

        var line1 = getParameterByName('line1');
        var line2 = getParameterByName('line2');
        if (line1 === null && line2 === null) return;

        // The shopper's own text wins; the server-rendered default ("YOUR TEXT")
        // doesn't, so the orientation switch can carry text across (2026-10-06).
        if ($line1.val() && $line1.val() !== $line1.prop('defaultValue')) return;

        if (line1 !== null) $line1.val(line1);
        if (line2 !== null) $line2.val(line2);
    }

    // Push whatever is in the visible fields into the preview and the hidden inputs
    // that actually submit. Covers the metafield default, a deep link, and values
    // the browser restores on back-navigation - none of which fire an input event.
    window.bbSyncFromFields = syncFromFields;

    function syncFromFields() {
        var line1 = $('.custom-line-1 input[type=text]').val() || "";
        var line2 = $('.custom-line-2 input[type=text]').val() || "";

        if (!line1 && !line2) return;

        setBadgeText(replaceKey(line1));
        setBadgeText(replaceKey(line2), true);
        $("#bb_option_custom_line_1").val(line1);
        $("#bb_option_custom_line_2").val(line2);
    }

    applyQueryParams();
    syncFromFields();

    // Only needed on a page still served by the app, where the fields arrive after
    // window.load. Server-rendered fields are present before this runs.
    var legacyContainer = document.getElementById('infiniteoptions-container');

    if (legacyContainer && window.MutationObserver) {
        var observer = new MutationObserver(function() {
            applyQueryParams();
            syncFromFields();
        });

        observer.observe(legacyContainer, {
            attributes: true,
            childList: true,
            characterData: true,
            subtree: true
        });
    }
});